// Safety Overview (2026-09-27) — the Project Overview and the Safety Dashboard as ONE
// page, to replace both. It opens as the Project Overview (no crash data); crash data,
// countermeasures and the statistics join it when loaded (later stages). A source is
// an overview (the server scopes it to the caller's projects) or a whole state.
// Every table sorts, pins and limits its rows; pins are kept per source.
// Modes (2026-10-22): the page opens on a start screen — the data, then a mode. "Full capabilities"
// (RDV, @rdvsystems.com accounts only) is everything below; the customer modes (Maintenance, Existing
// projects / rehab, New project) show the deficiency sites only, with the built-in rules, criteria
// and weights, three filters and two panels, and rank each analysis on its own by default.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import '../Safety/SafetyDashboard.css';
import '../Overview/ProjectOverview.css';
import { MultiPicker } from '../Safety/CorridorPicker';
import TopNSelect from '../Safety/TopNSelect';
import OverviewMap, { boundsOf } from '../Overview/OverviewMap';
import OverviewStrips from '../Overview/OverviewStrips';
import { ClustersTable, CrashClustersTable, HotspotsTable, SitesTable, SummaryTable } from '../Overview/OverviewTables';
import SettingsDialog, { loadDisabledRules, saveDisabledRules } from '../Safety/SettingsDialog';
import {
  DEFAULT_FILTERS as SAFETY_DEFAULTS, applyCorrelatedOnly, clusterDeficiencies, clusterVisibleCrashes, computeDescriptiveStats, computeSiteTable, filterCrashes, sortClusters,
} from '../Safety/safetyFilters';
import CrashFilters, { DEFAULT_CRASH_FILTERS } from './CrashFilters';
import FiltersDialog from './FiltersDialog';
import NetworkSources from './NetworkSources';
import RoadElementDeleteDialog from './RoadElementDeleteDialog';
import { availablePanels, buildPdfPanels } from './pdfPanels';
import { useServerPermission } from '../../utils/serverPermissions';
// Product analytics (RDV-only reporting; see docs/architecture/safety_dashboard_telemetry_plan.md).
// Aliased on import: this file already has its own setFilters/track names.
import { init as telemetryInit, track as telemetryTrack } from '../../utils/telemetry';
import { usePanelDwell, useTelemetryContext, useTrackedSettings } from '../../utils/useTelemetry';
import OffendersDialog from './OffendersDialog';
import StartScreen, { ModeSwitch, RdvMark } from './StartScreen';
import { HotspotSettings, MaintenanceBar, MaintenanceTasks, ProjectSegmentPicker } from './UseCaseControls';
import {
  HOTSPOT_DEFAULTS, MAINT_TASKS, buildHotspots, buildProspects, isFull, loadPref, loadRankBy, maintenanceScope, maintenanceSeverities, mapTopKey, mergeCurveDirections, modeDefaults, modeOf, rankWithinAnalysis,
  recallMode, rememberMode, saveRankBy, savePref, topPerAnalysis,
} from './modes';
import { clusterLogic, crashLogic, loadWeights, normalizeWeights, saveWeights, siteLogic, siteScore, siteScoreParts, stretchBreakdown } from '../Safety/offenders';
import ScoreDialog from './ScoreDialog';
import { deleteOverview } from '../Overview/overviewApi';
import {
  DEFAULT_RULES, GUARDRAIL_LABEL, NET_CONDITIONS, OBSTRUCTION_ANALYSES, OBSTRUCTION_LABELS, loadRules, normalizeRules, rulesHash, rulesParam, saveRules,
} from './rules';
import { computeImpactSummary, fmtUsd, kabcoOf, visibleFindings } from '../Safety/insightsText';
import { CMF_POLICIES, buildProgram, exposureYears, siteRecommendation } from '../Safety/cmfBenefit';
import CmfCard from '../Safety/CmfCard';
import InsightsPanel from '../Safety/InsightsPanel';
import ClusterInsightsPanel from '../Safety/ClusterInsightsPanel';
import IntersectionsPanel from '../Safety/IntersectionsPanel';
import ProgramPanel from '../Safety/ProgramPanel';
import VerdictCard, { VERDICT_OPTIONS } from '../Safety/VerdictCard';
import { CriteriaDialog, ManagerDialog, Modal, PdfDialog, REPORT_NAME, SimplePdfDialog } from '../Overview/OverviewDialogs';
import SiteNoteDialog from './SiteNoteDialog';
import { STATUS_LABEL, fetchSiteNotes, matchNotes, saveSiteNote } from './siteNotes';
import { CORRELATION_RULES } from '../Safety/safetyFilters';
import { fetchOverviews, mapLimit } from '../Overview/overviewApi';
import { DETAIL_ANALYSES, MAX_DETAIL_ANALYSES } from '../Overview/StationsLayer';
import {
  ANALYSES, ANALYSIS_COLORS, ANALYSIS_NAMES, ROAD_COLORS, SEVERITIES, clipSegment, clusterSites, dedupeSegments, filterSites, numberSegments,
  overviewStats, positionFn, segmentName, stripBlocks, summarize,
} from '../Overview/overviewModel';
import {
  applySeverity, applySeverityColors, classifyValue, defaultSeveritySettings, loadSeveritySettings, saveSeveritySettings,
} from '../Overview/severitySettings';
import {
  fetchAnalysis, fetchCmfLibrary, fetchIntersectionsSO, fetchVerdict, fetchCrashes, fetchStates, loadPins, loadPlaces, savePlaces, parseSource, recallSource, rememberSource, savePins, sourceCorridors, sourceId, sourceSegment,
  sourceStations, deleteRoadElement, fetchRightOfWay, fetchRowCheck,
} from './safetyOverviewApi';

const NET_TYPES_KEY = 'safetyOverview:netTypes';     // the road-feature types the map shows
// the customer modes use the built-in rules, criteria and worst-offender weights, whatever this browser saved;
// only a deficiency's minimum run length comes from Settings -> Filters (Yaron, 2026-11-01)
const CUSTOMER_SEVERITY = defaultSeveritySettings();
const CUSTOMER_WEIGHTS = normalizeWeights(null);
/** How many top sites / clusters the map shows (0 = all): the RDV mode keeps the user's choice,
 *  a customer mode starts on its default (Top 3) every time. */
function readMapTop(mode) {
  const d = modeDefaults(mode).mapTop;
  if (!isFull(mode)) return { sites: d, clusters: d, hotspots: modeDefaults(mode).hotspotsTop || 5 };
  try { return { sites: d, clusters: d, ...JSON.parse(localStorage.getItem(mapTopKey(mode)) || '{}') }; } catch (e) { return { sites: d, clusters: d }; }
}
const MIN_RANGE_MI = 0.01;                              // the shortest milepost range (~53 ft)

/** A path's points within a milepost range, with the point just outside each end, so a short
 *  range (shorter than the path's vertex spacing) still draws — and the map can fit it. */
function pointsInRange(points, [lo, hi]) {
  const idx = points.map((p, i) => [p[2], i]).filter(([mp]) => mp != null);
  const inside = idx.filter(([mp]) => mp >= lo && mp <= hi).map(([, i]) => i);
  const below = idx.filter(([mp]) => mp < lo).sort((a, b) => b[0] - a[0])[0];
  const above = idx.filter(([mp]) => mp > hi).sort((a, b) => a[0] - b[0])[0];
  const keep = new Set([...inside, ...(below ? [below[1]] : []), ...(above ? [above[1]] : [])]);
  return points.filter((_, i) => keep.has(i));
}

const NEAR_MI = 0.1;          // a crash "at" a site: within 0.1 mi of it (the Safety Dashboard's buffer)
const METRIC_LABEL = {
  SSD: 'speed deficit (mph)', PSD: 'speed deficit (mph)', GLR: 'glare minutes / yr', FSA: 'cross slope (%)',
  HCA: 'curve safety score (%)', GRW: 'embankment height (ft)', ISD: 'required ISD (ft)',
};

const SubCard = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="safety-subcard">
      <button type="button" className="safety-subcard-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className={`safety-section-chevron ${open ? 'open' : ''}`}>▸</span>{title}
      </button>
      {open && <div className="safety-subcard-body">{children}</div>}
    </div>
  );
};

// the defaults Yaron set (2026-09-27): severe deficiencies only
const DEFAULT_SEVERITIES = ['severe'];

// every panel below the map starts collapsed (Yaron, 2026-09-28)
// `track`: the telemetry target for this panel (utils/telemetry_catalog.py). One
// prop here instruments every panel at once — the expand/collapse is an event and
// the open body reports how long it was actually on screen, so "time in a panel"
// means open AND visible rather than merely mounted.
const SectionCard = ({ title, meta, tools, children, defaultOpen = false, track }) => {
  const [open, setOpen] = useState(defaultOpen);
  const dwellRef = usePanelDwell(track || '');
  const toggle = () => {
    if (track) telemetryTrack('click', track, open ? 'collapse' : 'expand');
    setOpen(!open);
  };
  return (
    <div className="safety-section">
      <div className="ov-section-head">
        <button type="button" className="safety-section-head ov-section-toggle" onClick={toggle} aria-expanded={open}>
          <span className={`safety-section-chevron ${open ? 'open' : ''}`}>▸</span>
          <span className="safety-section-title">{title}</span>
          {meta && <span className="ov-section-meta">{meta}</span>}
        </button>
        {open && tools && <div className="ov-section-tools">{tools}</div>}
      </div>
      {open && <div className="safety-section-body" ref={dwellRef}>{children}</div>}
    </div>
  );
};

const Seg = ({ value, onChange, options, label }) => (
  <div className="ov-seg" role="group" aria-label={label}>
    {options.map(([v, text]) => (
      <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)}>{text}</button>
    ))}
  </div>
);

/** A milepost typed by hand: digits and one point only, kept within [min, max] (the road, and the other end). */
function MpInput({ label, value, min, max, onChange }) {
  const [text, setText] = useState(value.toFixed(2));
  useEffect(() => { setText(value.toFixed(2)); }, [value]);
  const commit = () => {
    const x = parseFloat(text);
    if (!Number.isFinite(x)) { setText(value.toFixed(2)); return; }
    const v = Math.round(Math.min(Math.max(x, min), max) * 100) / 100;
    setText(v.toFixed(2));
    if (v !== value) onChange(v);
  };
  return (
    <input type="text" inputMode="decimal" className="so-mp-input" aria-label={label} value={text}
      title={`${label}: ${min.toFixed(2)}–${max.toFixed(2)}`}
      onChange={(e) => setText(e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'))}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
      onBlur={commit} />
  );
}

// rows of a table: Top N, All, or a number of the user's own (never more than the table holds)
const RowsSelect = ({ id, value, onChange, max, presets }) => (
  <label className="ov-inline" htmlFor={id}>Rows to show
    <TopNSelect id={id} value={value} onChange={onChange} max={max} presets={presets} />
  </label>
);
// the customer modes start on the Top 3: it is one of their choices
const CUSTOMER_PRESETS = [3, 5, 10, 25, 50];
// rehab shows one project segment at a time: its analysis path in one colour
const REHAB_PATH_COLOR = '#2563eb';

// opt: the sub line is the first to go when the panels would not fit beside the map
const Tile = ({ label, value, sub, wide, children, opt = false }) => (
  <div className={`ov-tile ${wide ? 'ov-tile-wide' : ''}`}>
    <span className="ov-tile-label">{label}</span>
    {value != null && <span className="ov-tile-value">{value}</span>}
    {sub && <span className={`ov-tile-sub ${opt ? 'so-opt' : ''}`}>{sub}</span>}
    {children}
  </div>
);

// a customer mode's map starts with the SSD stations and rays on, the ISD rays a layer to switch on
const CUSTOMER_STATIONS_ON = ['SSD'];

export default function SafetyOverview() {
  // the mode: none until the start screen picks one; atStart = the start screen is shown (the mode
  // and the loaded data stay behind it). The RDV mode is offered to @rdvsystems.com accounts only.
  const [mode, setMode] = useState(null);
  const [atStart, setAtStart] = useState(true);
  const canFull = useServerPermission('view_safety_overview_full').allowed;
  // whole states and every road: RDV only; a customer's Full tab works on the customer's projects
  const canAllRoads = useServerPermission('view_safety_all_roads').allowed;
  const full = isFull(mode);
  // Maintenance: the work (asked each time the mode opens)
  const [maintTask, setMaintTask] = useState(null);
  const scope = mode === 'maintenance' ? maintenanceScope(maintTask, {}) : null;
  const scopeKey = scope ? `${scope.analyses.join(',')}|${scope.obstructions.join(',')}` : '';
  // the customer modes' rules: the built-in ones, Maintenance narrowing SSD / ISD to its obstructions
  // New project spawning: what makes a hotspot (kept per user) and the hotspot the sites table narrows to
  const [hotspotCfg, setHotspotCfgState] = useState(() => ({ ...HOTSPOT_DEFAULTS, ...loadPref('hotspots', {}) }));
  const setHotspotCfg = (v) => { setHotspotCfgState(v); savePref('hotspots', v); };
  const [pickedHotspot, setPickedHotspot] = useState(null);
  const [overviews, setOverviews] = useState(null);
  const [states, setStates] = useState([]);
  const [canCreate, setCanCreate] = useState(false);
  const [sourceKey, setSourceKey] = useState(null);      // 'ov:<id>' | 'st:<state>'
  const [listError, setListError] = useState('');
  const [pins, setPins] = useState({ summary: [], clusters: [], sites: [], crashClusters: [], program: [] });
  // crash data: off until asked for; loaded per shown segment and kept while the source stays
  const [crashOn, setCrashOn] = useState(false);
  const [crashData, setCrashData] = useState({});        // segKey -> payload | {error}  (payload._rules = its rule set)
  // user rules (Settings -> Filters): edited live, sent to the server ~0.8 s after the last change
  const [rules, setRules] = useState(() => loadRules());
  const [baseLayer, setBaseLayer] = useState('street');      // the map's base layer: the PDF draws the same one
  const [savedRules, setAppliedRules] = useState(() => loadRules());
  const minRunKey = JSON.stringify(savedRules.minStations);
  const customerRules = useMemo(() => normalizeRules({ ...DEFAULT_RULES, minStations: savedRules.minStations,
    ...(scope ? { obstructions: scope.obstructions, obstructionsSightOnly: true } : {}) }), [scopeKey, minRunKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const appliedRules = full ? savedRules : customerRules;
  useEffect(() => {
    const t = setTimeout(() => setAppliedRules(saveRules(rules)), 800);
    return () => clearTimeout(t);
  }, [rules]);
  const rulesQ = useMemo(() => rulesParam(appliedRules), [appliedRules]);
  const rulesTag = useMemo(() => rulesHash(appliedRules), [appliedRules]);
  const rulesRef = useRef(rulesQ);
  rulesRef.current = rulesQ;
  const [corridorList, setCorridorList] = useState([]);
  // worst-offender weights (Settings -> Worst offenders) and remembered map places (per source)
  const [savedWeights, setWeights] = useState(() => loadWeights());
  const weights = full ? savedWeights : CUSTOMER_WEIGHTS;
  const [places, setPlaces] = useState([]);
  const [rulesLoad, setRulesLoad] = useState({ total: 0, done: 0 });
  const [crashLoad, setCrashLoad] = useState({ total: 0, done: 0 });
  const [crashFilters, setCrashFilters] = useState({ ...DEFAULT_CRASH_FILTERS });
  const [disabledRules, setDisabledRules] = useState(() => loadDisabledRules());
  const [rulesOpen, setRulesOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [menuOpen]);
  const crashToken = useRef(0);
  // stage 4: countermeasures, statistical support, metric profile, milepost range
  const [cmfLibrary, setCmfLibrary] = useState(null);
  const [cmfError, setCmfError] = useState('');
  const [unitCostOverrides, setUnitCostOverrides] = useState({});
  const [verdicts, setVerdicts] = useState({});          // segKey -> {verdict} | {error} | {loading}
  const [verdictShow, setVerdictShow] = useState([]);
  const [strips, setStrips] = useState({});              // `${analysis}|${segKey}` -> {P, N} strips
  const [mpRange, setMpRange] = useState(null);
  const [hiddenRays, setHiddenRays] = useState([]);          // ISD maneuver ids whose rays are off
  // the panels beside the map fit its height: step up the compaction until they do
  const statsRef = useRef(null);
  const [fitLevel, setFitLevel] = useState(0);
  const [loaded, setLoaded] = useState({});            // segKey -> payload
  const [progress, setProgress] = useState({ total: 0, done: 0, failed: [], phase: 'idle' });
  const [filters, setFilters] = useState({ analyses: null, segments: null, severities: [...DEFAULT_SEVERITIES], direction: '' });
  const [view, setView] = useState({ unify: false, axis: 'mp', colourBy: 'analysis', sumBy: 'segment', measure: 'sites', showPaths: true,
    rowsSites: 25, rowsClusters: 10, rowsSummary: 0, rowsCrashClusters: 25, minAnalyses: 2 });
  // sites and clusters open in their rank order: the order the map's Top N takes its head from
  const [sorts, setSorts] = useState({ summary: { key: 'total', dir: 'desc' }, clusters: { key: 'rank', dir: 'asc' },
    sites: { key: 'rank', dir: 'asc' }, crashClusters: { key: 'count', dir: 'desc' } });
  const [focus, setFocus] = useState(null);
  const [stations, setStations] = useState({});          // `${analysis}|${segKey}` -> {P: rows, N: rows}
  const [stationLoad, setStationLoad] = useState({ total: 0, done: 0 });
  const stationToken = useRef(0);
  const [dialog, setDialog] = useState(null);           // 'new' | 'edit' | 'pdf' | 'criteria'
  const [savedSeverity, setSeverity] = useState(() => loadSeveritySettings());
  const severity = full ? savedSeverity : CUSTOMER_SEVERITY;
  // the colours every component reads (SEVERITY_COLORS is shared): set while rendering, before the children read them
  useMemo(() => applySeverityColors(severity), [severity]);
  // rows of the two panels in a customer mode (Top 3 at the start of every visit)
  const [cview, setCview] = useState({ rowsSites: 3, rowsSummary: 3, rowsHotspots: 5, showPaths: false });
  const rowsOf = (k) => (full ? view[k] : cview[k]);
  const setRows = (k, n) => (full ? setView((v) => ({ ...v, [k]: n })) : setCview((c) => ({ ...c, [k]: n })));
  // the user's rank flag, per mode: each analysis ranked on its own, or one combined ranking
  const [rankBy, setRankByState] = useState('combined');
  const setRankBy = (v) => { setRankByState(v); if (mode) saveRankBy(mode, v); };
  const mapRef = useRef(null);
  const loadToken = useRef(0);

  const loadList = useCallback(async (preferKey) => {
    try {
      const [data, st] = await Promise.all([fetchOverviews(), fetchStates().catch(() => ({ states: [] }))]);
      // no demo data on this page: an overview made only of demo projects is left out
      const demo = new Set(st.demoProjects || []);
      data.overviews = (data.overviews || []).filter((o) => !(o.projects.length && o.projects.every((p) => demo.has(p.id))));
      setOverviews(data.overviews);
      setStates(st.states || []);
      setCanCreate(Boolean(data.canCreate));
      setListError('');
      const keys = [...(data.overviews || []).map((o) => sourceId('ov', o.id)), ...(st.states || []).map((x) => sourceId('st', x.id))];
      const wanted = [preferKey, recallSource()].find((k) => k && keys.includes(k));
      setSourceKey(wanted || keys[0] || null);
    } catch (e) {
      setOverviews([]);
      setListError(e.message);
    }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const source = useMemo(() => parseSource(sourceKey), [sourceKey]);
  const overview = useMemo(() => (source?.kind === 'ov' ? (overviews || []).find((o) => o.id === source.id) || null : null),
    [overviews, source]);
  const stateSource = useMemo(() => (source?.kind === 'st' ? states.find((x) => x.id === source.id) || null : null), [states, source]);
  const active = overview || stateSource;                  // the source being shown
  const projectSignature = overview ? overview.projects.map((p) => p.id).join(',') : '';

  // pins are kept per source, across visits (this browser)
  useEffect(() => { if (sourceKey) { setPins(loadPins(sourceKey)); setPlaces(loadPlaces(sourceKey)); } }, [sourceKey]);
  const changePlaces = (fn) => setPlaces((p) => { const next = fn(p); savePlaces(sourceKey, next); return next; });
  const addPlace = useCallback((ll) => changePlaces((p) => [...p, {
    id: `pl:${Date.now()}`, lat: ll.lat, lng: ll.lng, note: '', created: new Date().toISOString(),
  }]), [sourceKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const updatePlace = useCallback((id, note) => changePlaces((p) => p.map((x) => (x.id === id ? { ...x, note } : x))),
    [sourceKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const removePlace = useCallback((id) => changePlaces((p) => p.filter((x) => x.id !== id)),
    [sourceKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const deleteThisOverview = async () => {
    if (!overview || !window.confirm(`Delete the overview "${overview.name}"? Its projects are not affected.`)) return;
    try { await deleteOverview(overview.id); setSourceKey(null); loadList(); } catch (e) { window.alert(`Could not delete: ${e.message}`); }
  };
  const pinsFor = (table) => ({
    pins: pins[table],
    onPins: (next) => setPins((p) => {
      telemetryTrack('click', 'table.pin', table);
      const all = { ...p, [table]: next }; savePins(sourceKey, all); return all;
    }),
  });

  // Load: corridors per project, then one payload per corridor, a few at a time.
  useEffect(() => {
    if (!active) { setLoaded({}); return undefined; }
    if (!mode) return undefined;                 // the start screen: loaded once a mode is chosen
    rememberSource(sourceKey);
    loadToken.current += 1;
    const token = loadToken.current;
    const live = () => loadToken.current === token;
    setLoaded({});
    setStations({});
    setCrashData({});
    setVerdicts({});
    setStrips({});
    setMpRange(null);
    setFocus(null);
    setFilters((f) => ({ ...f, analyses: null, segments: null }));
    setProgress({ total: 0, done: 0, failed: [], phase: 'corridors' });
    (async () => {
      let corridors = [];
      let failed = [];
      try {
        ({ corridors, failed } = await sourceCorridors(source, overview));
      } catch (e) {
        failed = [e.message];
      }
      if (!live()) return;
      setCorridorList(corridors);
      setProgress({ total: corridors.length, done: 0, failed, phase: 'segments' });
      await mapLimit(corridors, 4, async (c) => {
        try {
          const segment = await sourceSegment(source, c, rulesRef.current);
          if (!live()) return;
          setLoaded((prev) => ({ ...prev, [c.key]: segment }));
          setProgress((p) => ({ ...p, done: p.done + 1 }));
        } catch (e) {
          if (live()) setProgress((p) => ({ ...p, done: p.done + 1, failed: [...p.failed, c.key.split('|')[1]] }));
        }
      });
      if (live()) setProgress((p) => ({ ...p, phase: 'ready' }));
    })();
    return () => { loadToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey, Boolean(active), projectSignature, Boolean(mode)]);

  const firstRules = useRef(true);
  useEffect(() => {
    if (firstRules.current) { firstRules.current = false; return undefined; }
    if (!active || !corridorList.length) return undefined;
    let live = true;
    setRulesLoad({ total: corridorList.length, done: 0 });
    mapLimit(corridorList, 4, async (c) => {
      try {
        const segment = await sourceSegment(source, c, rulesQ);
        if (live) setLoaded((prev) => ({ ...prev, [c.key]: segment }));
      } catch (e) { /* the previous payload stays */ }
      if (live) setRulesLoad((p) => ({ ...p, done: p.done + 1 }));
    }).then(() => { if (live) setRulesLoad({ total: 0, done: 0 }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesQ]);

  // Part B: a road's road network is fetched in the background on the server; while it is
  // pending the page shows the rest and asks again every 20 s (at most ~15 min)
  const [netPoll, setNetPoll] = useState(0);
  const pendingNet = corridorList.filter((c) => loaded[c.key]?.network?.pending).map((c) => c.key).join(',');
  useEffect(() => {
    if (!active || !pendingNet || netPoll > 45) return undefined;
    let live = true;
    const t = setTimeout(() => {
      const keys = pendingNet.split(',');
      mapLimit(corridorList.filter((c) => keys.includes(c.key)), 4, async (c) => {
        try {
          const segment = await sourceSegment(source, c, rulesQ);
          if (live) setLoaded((prev) => ({ ...prev, [c.key]: segment }));
        } catch (e) { /* asked again next round */ }
      }).then(() => { if (live) setNetPoll((n) => n + 1); });
    }, 20000);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingNet, netPoll, rulesQ, Boolean(active)]);
  useEffect(() => { setNetPoll(0); }, [rulesQ, sourceKey]);

  const segments = useMemo(() => numberSegments(applySeverity(dedupeSegments(Object.values(loaded)), severity))
    .sort((a, b) => a.road.localeCompare(b.road, undefined, { numeric: true }) || ((a.segment || 0) - (b.segment || 0))
      || ((a.mpMin || 0) - (b.mpMin || 0))), [loaded, severity]);
  // a Maintenance work offers its own analyses only
  const available = useMemo(() => ANALYSES.filter((a) => segments.some((s) => (s.analyses || []).includes(a))
    && (!scope || scope.analyses.includes(a))), [segments, scopeKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const analyses = useMemo(() => (filters.analyses ? filters.analyses.filter((a) => available.includes(a)) : available),
    [filters.analyses, available]);
  // Existing projects / rehab: one project segment (the first until one is picked)
  const rehabSeg = mode === 'rehab' ? ((filters.segments || []).find((k) => loaded[k]) || (segments[0] && segments[0].key) || null) : null;
  const segKeys = useMemo(() => (rehabSeg ? [rehabSeg] : filters.segments ? filters.segments.filter((k) => loaded[k]) : segments.map((s) => s.key)),
    [filters.segments, segments, loaded, rehabSeg]);
  const shownSegments = useMemo(() => segments.filter((s) => segKeys.includes(s.key)), [segments, segKeys]);
  // Part B: the road-network features the passing-zone rules used, per shown road, with their source
  const networkFeatures = useMemo(() => shownSegments.flatMap((s) => {
    const net = s.network;
    if (!net || !net.features) return [];
    return net.features.map((f) => {
      const rc = appliedRules.network.conditions[f.type] || {};
      return { ...f, id: `net:${s.key}:${f.type}:${f.mp}:${f.src}`, segName: segmentName(s), state: s.state,
        srcLabel: net.sources?.[f.src]?.label, approachFt: rc.approachFt, departFt: rc.departFt };
    });
  }), [shownSegments, appliedRules]);
  const hasNetwork = shownSegments.some((s) => s.network);
  // deleting a road element: Admins and SuperUsers (DOT, OSM), only Admins (our analyses); the server re-checks
  const canNet = useServerPermission('edit_road_network').allowed;
  const canNetOurs = useServerPermission('edit_road_network_analyses').allowed;
  const canDeleteNetwork = (src) => canAllRoads && (src === 'ours' ? canNetOurs : canNet);
  const [deleting, setDeleting] = useState(null);
  const [netEdits, setNetEdits] = useState(0);            // bumps after a delete / restore: the panel re-reads its list
  const refreshState = (stateId) => {
    // every shown road of that state: its road network (and so its rules, crashes, verdicts) changed
    const mine = corridorList.filter((c) => (c.state || String(c.key).split('|')[0]) === stateId);
    return mapLimit(mine, 4, async (c) => {
      try {
        const segment = await sourceSegment(source, c, rulesQ);
        setLoaded((prev) => ({ ...prev, [c.key]: segment }));
      } catch (e) { /* the previous payload stays */ }
    }).then(() => setNetEdits((n) => n + 1));
  };
  // which road-feature types the map draws ([] = all; display only — the rules use every feature)
  const [netTypes, setNetTypesState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(NET_TYPES_KEY) || '[]'); } catch (e) { return []; }
  });
  const setNetTypes = (next) => {
    setNetTypesState(next);
    try { localStorage.setItem(NET_TYPES_KEY, JSON.stringify(next)); } catch (e) { /* private window */ }
  };
  const netTypeOptions = useMemo(() => {
    const counts = {};
    networkFeatures.forEach((f) => { counts[f.type] = (counts[f.type] || 0) + 1; });
    return [...NET_CONDITIONS, ['guardrail', GUARDRAIL_LABEL]].filter(([k]) => counts[k])
      .map(([k, label]) => ({ key: k, label: `${label} · ${counts[k]}` }));
  }, [networkFeatures]);
  // the obstruction types of the loaded roads' deficient SSD stations and short ISD maneuvers, with counts
  const obstructionOptions = useMemo(() => {
    const counts = {};
    shownSegments.forEach((s) => Object.entries(s.obstructions || {}).forEach(([k, n]) => { counts[k] = (counts[k] || 0) + n; }));
    return Object.entries(counts).sort((a, b) => b[1] - a[1])
      .map(([k, n]) => ({ key: k, label: `${OBSTRUCTION_LABELS[k] || k} · ${n}` }));
  }, [shownSegments]);
  const mapNetworkTypes = useMemo(() => (netTypes.length ? networkFeatures.filter((f) => netTypes.includes(f.type)) : networkFeatures),
    [networkFeatures, netTypes]);
  const roadColors = useMemo(() => Object.fromEntries(
    Array.from(new Set(segments.map((s) => s.road))).map((r, i) => [r, ROAD_COLORS[i % ROAD_COLORS.length]])), [segments]);

  // HCA: one curve for both directions, or each direction apart (the user's choice, kept per user)
  const [curvesByDirection, setCurvesByDirectionState] = useState(() => Boolean(loadPref('curvesByDirection', false)));
  const setCurvesByDirection = (v) => { setCurvesByDirectionState(v); savePref('curvesByDirection', v); };
  const baseSites = useMemo(() => {
    if (!analyses.length) return [];
    // a customer mode shows no HCA "curve" where the road barely bends; the RDV mode keeps them, marked, for QA
    const list = filterSites(segments, { analyses, segments: segKeys, severities: filters.severities, direction: filters.direction })
      .filter((s) => full || !s.straightLine);
    return curvesByDirection ? list : mergeCurveDirections(list);
  }, [segments, analyses, segKeys, filters.severities, filters.direction, curvesByDirection, full]);

  // how many sites each analysis has under the other filters: an analysis with none is greyed in the picker
  const analysisCounts = useMemo(() => {
    const counts = {};
    filterSites(segments, { analyses: available, segments: segKeys, severities: filters.severities, direction: filters.direction })
      .filter((s) => full || !s.straightLine)
      .forEach((s) => {
        if (mpRange && s.mpStart != null && !((s.mpEnd ?? s.mpStart) >= mpRange[0] && s.mpStart <= mpRange[1])) return;
        counts[s.analysis] = (counts[s.analysis] || 0) + 1;
      });
    return counts;
  }, [segments, available, segKeys, filters.severities, filters.direction, mpRange, full]);

  // ---- crash data (loaded on demand, for the roads shown) ----
  const segKeySignature = segKeys.join(',');
  // crash data and verdicts follow the rules AND the road network they were built on
  const segTag = (s) => `${rulesTag}|${s.network?.stamp || (s.network?.pending ? 'pending' : '')}`;
  const netSignature = shownSegments.map((s) => s.network?.stamp || '').join(',');
  useEffect(() => {
    if (!crashOn || !active) { setCrashLoad({ total: 0, done: 0 }); return undefined; }
    crashToken.current += 1;
    const token = crashToken.current;
    const live = () => crashToken.current === token;
    const missing = shownSegments.filter((s) => !crashData[s.key] || crashData[s.key]._rules !== segTag(s));
    if (!missing.length) { setCrashLoad({ total: 0, done: 0 }); return undefined; }
    setCrashLoad({ total: missing.length, done: 0 });
    mapLimit(missing, 3, async (s) => {
      let payload;
      try { payload = await fetchCrashes(s, rulesQ); } catch (e) { payload = { error: e.message }; }
      if (!live()) return;
      setCrashData((prev) => ({ ...prev, [s.key]: { ...payload, _rules: segTag(s) } }));
      setCrashLoad((p) => ({ ...p, done: p.done + 1 }));
    }).then(() => { if (live()) setCrashLoad({ total: 0, done: 0 }); });
    return () => { crashToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crashOn, sourceKey, segKeySignature, segments.length, rulesTag, netSignature]);
  const crashPayloads = useMemo(() => shownSegments.map((s) => [s, crashData[s.key]]).filter(([, p]) => p && !p.error),
    [shownSegments, crashData]);
  const crashFailed = useMemo(() => shownSegments.filter((s) => crashData[s.key]?.error).map((s) => segmentName(s)),
    [shownSegments, crashData]);
  const firstCrashPayload = crashPayloads.length ? crashPayloads[0][1] : null;
  const allCrashes = useMemo(() => (crashOn ? crashPayloads.flatMap(([s, p]) => (p.crashes || []).map((c) => ({
    ...c, crashId: c.id, id: `crash:${s.key}:${c.id}`, lng: c.lon, corridor: s.key, segKey: s.key, road: segmentName(s),
  }))) : []), [crashOn, crashPayloads]);
  const crashAnalysis = analyses.length === 1 ? analyses[0] : 'ALL';
  const asRuns = (list) => list.map((s) => ({ ...s, corridor: s.segKey }));
  // milepost range: offered when the roads shown are ONE road (a milepost means nothing across roads)
  const shownRoads = useMemo(() => Array.from(new Set(shownSegments.map((s) => s.road))), [shownSegments]);
  const roadExtent = useMemo(() => (shownRoads.length === 1 && shownSegments.length
    ? [Math.min(...shownSegments.map((s) => s.mpMin ?? Infinity)), Math.max(...shownSegments.map((s) => s.mpMax ?? -Infinity))] : null),
  [shownRoads, shownSegments]);
  // the milepost range: the RDV mode's, and rehab's project limits
  const range = (full || mode === 'rehab' || mode === 'newProject') && roadExtent && mpRange ? mpRange : null;
  // move one end of the milepost range; the range never gets shorter than MIN_RANGE_MI (equal ends showed nothing)
  const setRangeEnd = (end, v) => {
    const [lo0, hi0] = range || roadExtent;
    const span = Math.min(MIN_RANGE_MI, roadExtent[1] - roadExtent[0]);
    if (end === 0) {
      const lo = Math.max(roadExtent[0], Math.min(v, roadExtent[1] - span));
      setMpRange([lo, Math.max(hi0, lo + span)]);
    } else {
      const hi = Math.min(roadExtent[1], Math.max(v, roadExtent[0] + span));
      setMpRange([Math.min(lo0, hi - span), hi]);
    }
  };
  // the map follows the milepost range: the analysis paths are cut to it, and the map fits it
  const mapSegments = useMemo(() => (!range ? shownSegments : shownSegments.map((s) => ({
    ...s,
    directions: Object.fromEntries(Object.entries(s.directions || {}).map(([dir, d]) => [dir, {
      ...d, points: pointsInRange(d.points || [], range),
    }])),
  }))), [shownSegments, range]);
  const mapNetwork = useMemo(() => (!range ? mapNetworkTypes
    : mapNetworkTypes.filter((f) => f.mp <= range[1] && (f.mpEnd ?? f.mp) >= range[0])), [mapNetworkTypes, range]);
  useEffect(() => { setMpRange(null); }, [shownRoads.join('|')]);   // eslint-disable-line react-hooks/exhaustive-deps
  // statistical support: the analyses whose verdict is "Above chance" on their road
  const assoc = useMemo(() => {
    const out = new Set();
    Object.entries(verdicts).forEach(([k, v]) => {
      Object.entries(v?.verdict?.perAnalysis || {}).forEach(([a, x]) => { if (x.verdict === 'associated') out.add(`${k}|${a}`); });
    });
    return out;
  }, [verdicts]);
  // ---- sight line vs right of way (2026-10-25): does a blocked sight line stay inside the state's right of way
  const [rowChecks, setRowChecks] = useState({});      // segKey -> {SSD: {P|N: [[mp, ft]]}, ISD: {id: ft}, toleranceFt}
  useEffect(() => {
    if (!mode) return undefined;
    let live = true;
    const missing = segments.filter((s) => !(s.key in rowChecks) && (s.analyses || []).some((a) => a === 'SSD' || a === 'ISD'));
    if (!missing.length) return undefined;
    setRowChecks((m) => ({ ...m, ...Object.fromEntries(missing.map((s) => [s.key, null])) }));
    mapLimit(missing, 2, async (s) => {
      let r = null;
      try { r = await fetchRowCheck(s); } catch (e) { /* no flag rather than an error */ }
      if (live) setRowChecks((m) => ({ ...m, [s.key]: r && r.available ? r : null }));
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments.map((s) => s.key).join(','), Boolean(mode)]);
  useEffect(() => { setRowChecks({}); }, [sourceKey]);
  const rowFlags = useMemo(() => {
    const out = {};
    baseSites.forEach((s) => {
      const rc = rowChecks[s.segKey];
      if (!rc) return;
      let pct = null;          // how much of its most exposed sight line lies inside the right of way
      if (s.analysis === 'SSD') {
        const lo = (s.mpStart ?? 0) - 0.002;
        const hi = (s.mpEnd ?? s.mpStart ?? 0) + 0.002;
        ((rc.SSD || {})[s.direction] || []).forEach(([mp, , inside]) => { if (mp >= lo && mp <= hi) pct = Math.min(pct ?? 100, inside); });
      } else if (s.analysis === 'ISD') {
        (s.maneuvers || []).forEach((m) => { const inside = (rc.ISDPct || {})[m.id]; if (inside != null) pct = Math.min(pct ?? 100, inside); });
      }
      if (pct != null) out[s.id] = { pct };
    });
    return out;
  }, [baseSites, rowChecks]);
  // the PDF: the same figure
  const pdfRowFlags = useMemo(() => Object.fromEntries(Object.entries(rowFlags).map(([id, f]) => [id, `${f.pct}%`])), [rowFlags]);

  // the Right of way filter: every deficiency, 100% in the right of way (the sight-distance sites whose sight line
  // lies wholly inside, and every other site: they lie on the road), or under 100%
  const [rowFilter, setRowFilter] = useState('all');
  const { sites, crashes } = useMemo(() => {
    let kept = range ? baseSites.filter((s) => s.mpStart != null && (s.mpEnd ?? s.mpStart) >= range[0] && s.mpStart <= range[1]) : baseSites;
    if (rowFilter !== 'all') {
      kept = kept.filter((s) => {
        const f = rowFlags[s.id];
        if (rowFilter === 'outside') return Boolean(f && f.pct < 100);
        return !f || f.pct >= 100;
      });
    }
    if (!crashOn) return { sites: kept, crashes: null };
    const f = { ...SAFETY_DEFAULTS, ...crashFilters, disabledRules, mpRange: range, dateRange: null,
      direction: filters.direction || 'both', analyses };
    let shown = filterCrashes(allCrashes, f);
    if (crashFilters.aboveChanceOnly) {
      kept = kept.filter((s) => assoc.has(`${s.segKey}|${s.analysis}`));
      shown = shown.map((c) => ({ ...c, findings: Object.fromEntries(Object.entries(c.findings || {})
        .filter(([a]) => assoc.has(`${c.segKey}|${a}`))) })).filter((c) => Object.keys(c.findings).length > 0);
    }
    if (crashFilters.correlatedOnly) {
      const c = applyCorrelatedOnly(asRuns(kept), shown, crashAnalysis);
      shown = c.crashes;
      const ids = new Set(c.runs.map((r) => r.id));
      kept = kept.filter((s) => ids.has(s.id));
    }
    return { sites: kept, crashes: shown };
  }, [crashOn, baseSites, allCrashes, crashFilters, disabledRules, filters.direction, analyses, crashAnalysis, range, assoc, rowFilter, rowFlags]);
  const baseSiteRows = useMemo(() => (crashes ? computeSiteTable(asRuns(sites), crashes) : sites), [sites, crashes]);

  // ---- countermeasures (CMF) ----
  useEffect(() => {
    if (!crashOn || cmfLibrary || cmfError) return;
    fetchCmfLibrary().then((r) => setCmfLibrary(r.library || null)).catch((e) => setCmfError(e.message));
  }, [crashOn, cmfLibrary, cmfError]);
  const costTable = firstCrashPayload?.costTable || null;
  const costs = useMemo(() => costTable?.values || {}, [costTable]);
  const exposure = useMemo(() => exposureYears(firstCrashPayload?.datasetYears, crashFilters.years), [firstCrashPayload, crashFilters.years]);
  const cmfActive = Boolean(crashes && crashFilters.showCmf && cmfLibrary);
  const recommendations = useMemo(() => (cmfActive ? baseSiteRows.map((s) => siteRecommendation(
    { ...s, corridor: s.segKey, mpEnd: s.mpEnd ?? s.mpStart, road: s.segment ? `${s.road} · ${s.segment}` : s.road },
    crashes, cmfLibrary,
    { policy: crashFilters.cmfPolicy, exposure: exposure || 1, costs, ratePct: crashFilters.discountRate, unitCostOverrides },
  )) : []), [cmfActive, baseSiteRows, crashes, cmfLibrary, crashFilters.cmfPolicy, crashFilters.discountRate, exposure, costs, unitCostOverrides]);
  const siteRows = useMemo(() => {
    if (!cmfActive) return baseSiteRows;
    const byId = new Map(recommendations.map((r) => [r.id, r]));
    return baseSiteRows.map((s) => ({ ...s, cmf: byId.get(s.id) }));
  }, [cmfActive, baseSiteRows, recommendations]);
  const program = useMemo(() => (cmfActive ? buildProgram(recommendations) : null), [cmfActive, recommendations]);
  const policyLabel = (CMF_POLICIES.find((p) => p.key === crashFilters.cmfPolicy) || CMF_POLICIES[0]).label;
  const economics = useMemo(() => {
    if (!crashes) return null;
    const correlated = crashes.filter((c) => visibleFindings(c, crashAnalysis).length > 0);
    const s = computeImpactSummary(correlated, costTable);
    return { correlated: s.n, cost: s.cost, lives: s.lives, benefitPerYr: program ? program.totals.benefitPerYr : null };
  }, [crashes, crashAnalysis, costTable, program]);
  const correlatedCount = useMemo(() => (crashes || []).filter((c) => visibleFindings(c, crashAnalysis).length > 0).length,
    [crashes, crashAnalysis]);
  const setUnitCost = (siteKey, crfid, value) => setUnitCostOverrides((o) => ({ ...o, [`${siteKey}:${crfid}`]: value }));

  // ---- statistical support: one verdict per road with crash data ----
  const verdictSignature = crashPayloads.map(([s]) => s.key).join(',');
  useEffect(() => {
    if (!crashOn) return undefined;
    let live = true;
    const missing = crashPayloads.map(([s]) => s).filter((s) => !verdicts[s.key]
      || (verdicts[s.key]._rules !== segTag(s) && !verdicts[s.key].loading));
    if (!missing.length) return undefined;
    setVerdicts((v) => ({ ...v, ...Object.fromEntries(missing.map((s) => [s.key, { ...(v[s.key] || {}), loading: true }])) }));
    mapLimit(missing, 3, async (s) => {
      let out;
      try { out = { verdict: (await fetchVerdict(s, false, rulesQ)).verdict }; } catch (e) { out = { error: e.message }; }
      if (live) setVerdicts((v) => ({ ...v, [s.key]: { ...out, _rules: segTag(s) } }));
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crashOn, verdictSignature, rulesTag, netSignature]);
  const recomputeVerdict = (s) => {
    setVerdicts((v) => ({ ...v, [s.key]: { ...(v[s.key] || {}), recomputing: true } }));
    fetchVerdict(s, true, rulesQ).then((r) => setVerdicts((v) => ({ ...v, [s.key]: { verdict: r.verdict, _rules: segTag(s) } })))
      .catch((e) => setVerdicts((v) => ({ ...v, [s.key]: { error: e.message } })));
  };
  const hasVerdicts = Object.values(verdicts).some((v) => v?.verdict);

  // ---- one analysis selected: its value profile along each road (metric lane of the strips) ----
  const metricAnalysis = full && analyses.length === 1 ? analyses[0] : null;
  const stripSignature = `${metricAnalysis}|${rulesTag}|${shownSegments.map((s) => s.key).join(',')}`;
  useEffect(() => {
    if (!metricAnalysis) return undefined;
    let live = true;
    const missing = shownSegments.filter((s) => (s.analyses || []).includes(metricAnalysis) && !strips[`${metricAnalysis}|${rulesTag}|${s.key}`]);
    if (!missing.length) return undefined;
    mapLimit(missing, 3, async (s) => {
      const pair = {};
      for (const d of Object.keys(s.directions || {})) {
        // eslint-disable-next-line no-await-in-loop
        try { pair[d] = (await fetchAnalysis(s, d, metricAnalysis, rulesQ)).strip || null; } catch (e) { pair[d] = null; }
      }
      if (live) setStrips((x) => ({ ...x, [`${metricAnalysis}|${rulesTag}|${s.key}`]: pair }));
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripSignature]);
  const metric = useMemo(() => {
    if (!metricAnalysis) return null;
    const bins = {};
    let binMi = 0.01;
    shownSegments.forEach((s) => {
      const pair = strips[`${metricAnalysis}|${rulesTag}|${s.key}`];
      if (!pair) return;
      const byMp = new Map();
      Object.entries(pair).forEach(([d, strip]) => {
        if (!strip || (filters.direction && d !== filters.direction)) return;
        binMi = strip.binMi || binMi;
        (strip.bins || []).forEach((b) => {
          if (range && (b.mp < range[0] || b.mp > range[1])) return;
          const prev = byMp.get(b.mp);
          if (prev === undefined || Math.abs(b.value) > Math.abs(prev)) byMp.set(b.mp, b.value);   // the worse direction
        });
      });
      bins[s.key] = Array.from(byMp.entries()).sort((a, b) => a[0] - b[0]).map(([mp, value]) => ({ mp, value }));
    });
    return { analysis: metricAnalysis, label: METRIC_LABEL[metricAnalysis] || 'value', binMi, bins };
  }, [metricAnalysis, shownSegments, strips, filters.direction, range, rulesTag]);
  const crashStats = useMemo(() => (crashes
    ? computeDescriptiveStats(crashes, asRuns(sites), null, crashAnalysis) : null), [crashes, sites, crashAnalysis]);
  const crashClusters = useMemo(() => {
    if (!crashes) return [];
    const byId = new Map(crashes.map((c) => [c.crashId, c]));
    return clusterVisibleCrashes(crashes.map((c) => ({ ...c, id: c.crashId }))).map((cl) => {
      const members = cl.crashIds.map((id) => byId.get(id)).filter(Boolean);
      const manners = {};
      members.forEach((m) => { manners[m.manner] = (manners[m.manner] || 0) + 1; });
      const near = sites.filter((s) => s.segKey === cl.corridor && s.mpStart != null
        && cl.mpStart <= (s.mpEnd ?? s.mpStart) + NEAR_MI && cl.mpEnd >= s.mpStart - NEAR_MI);
      const years = members.map((m) => m.year).filter(Boolean);
      return {
        ...cl, id: `ccl:${cl.corridor}|${cl.mpStart}`, segKey: cl.corridor, road: members[0]?.road || cl.road,
        members: [...members].sort((a, b) => a.milepost - b.milepost), nearSites: near,
        mix: { fatal: members.filter((m) => m.severityClass === 'fatal').length,
          injury: members.filter((m) => m.severityClass === 'injury').length,
          pdo: members.filter((m) => m.severityClass === 'pdo').length },
        years: years.length ? (Math.min(...years) === Math.max(...years) ? `${Math.min(...years)}` : `${Math.min(...years)}–${Math.max(...years)}`) : '',
        ki: members.filter((m) => m.severityClass === 'fatal' || m.severityClass === 'injury').length,
        leading: Object.entries(manners).sort((a, b) => b[1] - a[1])[0]?.[0] || '',
        towns: Array.from(new Set(members.map((m) => m.city).filter(Boolean))).join(', '),
        hit: near.length > 0, hitAnalyses: Array.from(new Set(near.map((s) => s.analysis))),
      };
    });
  }, [crashes, sites]);
  const crashesNear = (site) => (crashes || []).filter((c) => c.segKey === site.segKey
    && c.milepost >= site.mpStart - NEAR_MI && c.milepost <= (site.mpEnd ?? site.mpStart) + NEAR_MI);
  const allClusters = useMemo(() => clusterSites(sites), [sites]);
  const clusters = useMemo(() => allClusters.filter((c) => c.n >= view.minAnalyses), [allClusters, view.minAnalyses]);
  const position = useMemo(() => positionFn(shownSegments, view.axis, view.unify), [shownSegments, view.axis, view.unify]);
  const blocks = useMemo(() => stripBlocks(shownSegments, view.unify), [shownSegments, view.unify]);
  const summaryRows = useMemo(() => summarize(shownSegments, sites, view.sumBy, analyses, filters.direction),
    [shownSegments, sites, view.sumBy, analyses, filters.direction]);

  // ONE analysis selected -> the map shows that analysis the way its report does
  // (station dots, glare bands, slope triangles, sight rays). Loaded per corridor, on demand.
  // Up to three analyses keep their report look together; beyond that the map goes back to coloured runs.
  // (the customer modes draw the deficiency sites only: no stations)
  const details = useMemo(() => (full && analyses.length >= 1 && analyses.length <= MAX_DETAIL_ANALYSES ? analyses : []), [analyses, full]);
  // the customer modes draw no stations, but the SSD and ISD sight rays when those are shown (seen when zoomed in)
  const stationAnalyses = useMemo(() => (full ? details.filter((a) => DETAIL_ANALYSES.includes(a))
    : ['SSD', 'ISD'].filter((a) => analyses.includes(a))), [details, full, analyses]);
  const detail = stationAnalyses.join(',');
  const wantsStations = stationAnalyses.length > 0;
  const shownKeySignature = shownSegments.map((s) => s.key).join(',');
  useEffect(() => {
    if (!active || !wantsStations) { setStationLoad({ total: 0, done: 0 }); return undefined; }
    stationToken.current += 1;
    const token = stationToken.current;
    const live = () => stationToken.current === token;
    const missing = stationAnalyses.flatMap((a) => shownSegments
      .filter((s) => (s.analyses || []).includes(a) && !stations[`${a}|${s.key}`]).map((s) => ({ a, s })));
    if (!missing.length) { setStationLoad({ total: 0, done: 0 }); return undefined; }
    setStationLoad({ total: missing.length, done: 0 });
    mapLimit(missing, 4, async ({ a, s }) => {
      let detailOf = { directions: {}, reports: {} };
      try { detailOf = (await sourceStations(source, s, a)) || detailOf; } catch (e) { /* drawn without detail */ }
      if (!live()) return;
      setStations((prev) => ({ ...prev, [`${a}|${s.key}`]: { directions: detailOf.directions || {}, reports: detailOf.reports || {} } }));
      setStationLoad((p) => ({ ...p, done: p.done + 1 }));
    }).then(() => { if (live()) setStationLoad({ total: 0, done: 0 }); });
    return () => { stationToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey, detail, wantsStations, shownKeySignature]);
  const stationLayers = useMemo(() => {
    if (!wantsStations) return null;
    // one chunk per corridor and direction: each has its own report page
    return stationAnalyses.map((a) => {
      const chunks = [];
      shownSegments.forEach((s) => {
        const held = stations[`${a}|${s.key}`];
        if (!held) return;
        Object.entries(held.directions).forEach(([d, rows]) => {
          if (filters.direction && d !== filters.direction) return;
          // GRW: the existing guardrails of this side (road network), so shielded warrants draw as such
          const shield = a === 'GRW' && !appliedRules.grwShield.ignoreInventory
            ? ((s.network && s.network.features) || []).filter((f) => f.type === 'guardrail' && (f.side === d || f.side === 'both'))
              .map((f) => [f.mp, f.mpEnd ?? f.mp]) : null;
          // SSD / PSD / GRW: the deficiency runs of this analysis and side under the rules (every severity:
          // the severity filter only hides runs), so a deficient dot outside them draws hollow — too short to count
          const runs = ['SSD', 'PSD', 'GRW'].includes(a)
            ? (s.sites || []).filter((x) => x.analysis === a && x.direction === d && x.mpStart != null)
              .map((x) => [x.mpStart, x.mpEnd ?? x.mpStart]) : undefined;
          chunks.push({ rows, report: held.reports[d] || null, label: `${segmentName(s)} · ${d === 'P' ? 'positive' : 'negative'} direction`, shield, runs, segKey: s.key, direction: d });
        });
      });
      return { analysis: a, chunks };
    });
  }, [wantsStations, stationAnalyses, shownSegments, stations, filters.direction, appliedRules.grwShield.ignoreInventory]);

  // ISD rays: switched on and off per intersection and maneuver, from the intersection's popup
  const obstructionKeep = appliedRules.obstructions.length ? appliedRules.obstructions : null;
  const shownStationLayers = useMemo(() => (stationLayers
    ? stationLayers
      // the Obstruction filter narrowed: analyses without an obstruction draw nothing, SSD / ISD only the kept types
      .filter((l) => !obstructionKeep || OBSTRUCTION_ANALYSES.includes(l.analysis))
      .map((l) => ({ ...l, ...(l.analysis === 'ISD' ? { hiddenRays } : {}), ...(obstructionKeep ? { obstructions: obstructionKeep } : {}) }))
    : stationLayers),
  [stationLayers, hiddenRays, obstructionKeep]);
  const isdControl = useMemo(() => ({
    hidden: hiddenRays,
    toggle: (id) => setHiddenRays((h) => (h.includes(id) ? h.filter((x) => x !== id) : [...h, id])),
    setMany: (ids, hide) => setHiddenRays((h) => (hide ? Array.from(new Set([...h, ...ids])) : h.filter((x) => !ids.includes(x)))),
    only: (ids, keep) => setHiddenRays((h) => Array.from(new Set([...h.filter((x) => !ids.includes(x)), ...ids.filter((x) => x !== keep)]))),
    reset: () => setHiddenRays([]),
  }), [hiddenRays]);

  const intersections = useMemo(() => shownSegments.flatMap((s) => (s.intersections || [])
    .map((ix) => ({ ...ix, segKey: s.key, segName: segmentName(s) })))
    .filter((ix) => !range || (ix.mp >= range[0] && ix.mp <= range[1])), [shownSegments, range]);

  // Cluster insights over THE deficiency clusters (the multi-deficiency ones above), with the
  // crashes on each and its best-value countermeasure once crash data is loaded
  const clusterCards = useMemo(() => clusters.map((c) => {
    const runs = c.items.map((s) => ({ ...s, corridor: s.segKey, mpEnd: s.mpEnd ?? s.mpStart, worstSeverity: s.severity }));
    const groups = clusterDeficiencies(runs, crashes || [], { gapMi: NEAR_MI, costs, minRuns: 1 });
    const g = groups.sort((a, b) => b.runCount - a.runCount)[0];
    return g ? { ...g, id: c.id, lat: c.lat, lng: c.lng, road: segmentName(c) } : null;
  }).filter(Boolean), [clusters, crashes, costs]);

  // ---- one rank for the map and the panels: the map's Top N is the head of the same ranked list,
  // after every filter (so "Top 10 on the map" = the first 10 rows of the sites table / cluster insights)
  const [mapTop, setMapTopState] = useState(() => readMapTop('full'));
  const setMapTop = (patch) => setMapTopState((m) => {
    const next = { ...m, ...patch };
    if (full) { try { localStorage.setItem(mapTopKey(mode), JSON.stringify(next)); } catch (e) { /* private window */ } }
    return next;
  });
  const rankedSites = useMemo(() => {
    const bySeg = new Map();
    (crashes || []).forEach((c) => { if (!bySeg.has(c.segKey)) bySeg.set(c.segKey, []); bySeg.get(c.segKey).push(c); });
    const scored = sites.map((s) => {
      let cost = 0;
      let correlated = 0;
      (bySeg.get(s.segKey) || []).forEach((c) => {
        if (c.milepost >= s.mpStart - NEAR_MI && c.milepost <= (s.mpEnd ?? s.mpStart) + NEAR_MI) {
          cost += costs[kabcoOf(c)] || 0;
          if ((c.findings || {})[s.analysis]) correlated += 1;
        }
      });
      return [s, siteScore(s, { cost, correlated }, weights)];
    });
    return scored.sort((a, b) => b[1] - a[1] || (b[0].blockedPct || 0) - (a[0].blockedPct || 0) || (b[0].lengthMi || 0) - (a[0].lengthMi || 0) || (a[0].mpStart ?? 0) - (b[0].mpStart ?? 0))
      .map(([s]) => s);
  }, [sites, crashes, costs, weights]);
  const combinedRank = useMemo(() => new Map(rankedSites.map((s, i) => [s.id, i + 1])), [rankedSites]);
  // ranked per analysis: each analysis' sites numbered on their own (the same score orders them)
  const perAnalysis = useMemo(() => rankWithinAnalysis(rankedSites, analyses), [rankedSites, analyses]);
  const byAnalysisRank = rankBy === 'analysis';
  const siteRank = byAnalysisRank ? perAnalysis.rank : combinedRank;
  const siteRankOrder = byAnalysisRank ? perAnalysis.order : null;
  const rankedClusters = useMemo(() => {
    const order = sortClusters(clusterCards, 'worst', weights).map((c) => c.id);
    const byId = new Map(clusters.map((c) => [c.id, c]));
    const seen = new Set(order);
    return [...order.map((id) => byId.get(id)).filter(Boolean),
      ...clusters.filter((c) => !seen.has(c.id)).sort((a, b) => (a.mpStart ?? 0) - (b.mpStart ?? 0))];
  }, [clusters, clusterCards, weights]);
  const clusterRank = useMemo(() => new Map(rankedClusters.map((c, i) => [c.id, i + 1])), [rankedClusters]);
  // the map: the top N of each (pinned rows always too), or everything
  const topOf = (ranked, n, pinnedIds) => {
    if (!n) return ranked;
    const head = ranked.slice(0, n);
    const ids = new Set(head.map((x) => x.id));
    return [...head, ...ranked.filter((x) => pinnedIds.includes(x.id) && !ids.has(x.id))];
  };
  const mapSites = useMemo(() => (byAnalysisRank
    ? topPerAnalysis(perAnalysis.byAnalysis, mapTop.sites, pins.sites || [])
    : topOf(rankedSites, mapTop.sites, pins.sites || [])), [byAnalysisRank, perAnalysis, rankedSites, mapTop.sites, pins.sites]); // eslint-disable-line react-hooks/exhaustive-deps
  const mapClusters = useMemo(() => topOf(rankedClusters, mapTop.clusters, pins.clusters || []), [rankedClusters, mapTop.clusters, pins.clusters]); // eslint-disable-line react-hooks/exhaustive-deps
  const onMapSites = useMemo(() => (mapTop.sites ? new Set(mapSites.map((s) => s.id)) : null), [mapTop.sites, mapSites]);
  const onMapClusters = useMemo(() => (mapTop.clusters ? new Set(mapClusters.map((c) => c.id)) : null), [mapTop.clusters, mapClusters]);
  // ---- New project spawning: hotspots (stretches where deficiency sites follow each other closely)
  const isNewProject = mode === 'newProject';
  const segByKey = useMemo(() => new Map(segments.map((s) => [s.key, s])), [segments]);
  const byLength = hotspotCfg.findBy === 'length';
  const hsPrefix = byLength ? 'P' : 'H';
  const rankedHotspots = useMemo(() => (isNewProject
    ? (byLength
      // the best stretches of the project length (only what lies inside a stretch counts), within the search area
      ? buildProspects(sites, hotspotCfg, (s) => siteScore(s, {}, weights), (k) => {
        const seg = segByKey.get(k);
        if (!seg) return null;
        return range ? [Math.max(seg.mpMin ?? range[0], range[0]), Math.min(seg.mpMax ?? range[1], range[1])] : [seg.mpMin ?? 0, seg.mpMax ?? 0];
      })
      : buildHotspots(sites, hotspotCfg, (s) => siteScore(s, {}, weights))).map((h, i) => {
      const seg = segByKey.get(h.segKey);
      const dirs = (seg && seg.directions) || {};
      const d = dirs[h.direction || 'P'] || dirs.P || dirs.N || Object.values(dirs)[0];
      const coords = d ? pointsInRange(d.points || [], [h.mpStart, h.mpEnd]).filter((p) => p[0] != null).map((p) => [p[0], p[1]]) : [];
      return { ...h, rank: i + 1, label: `${byLength ? 'P' : 'H'}${i + 1}`, coords, road: seg ? seg.road : h.road, segment: seg ? seg.segment : h.segment };
    }) : []), [isNewProject, byLength, sites, hotspotCfg, weights, segByKey, range]);
  const hotspotRank = useMemo(() => new Map(rankedHotspots.map((h) => [h.id, h.rank])), [rankedHotspots]);
  const mapHotspots = useMemo(() => topOf(rankedHotspots, mapTop.hotspots || 0, []), [rankedHotspots, mapTop.hotspots]); // eslint-disable-line react-hooks/exhaustive-deps
  const picked = rankedHotspots.find((h) => h.id === pickedHotspot) || null;
  useEffect(() => { if (pickedHotspot && !picked) setPickedHotspot(null); }, [pickedHotspot, picked]);
  // New project's panels follow the hotspots the map shows: each site belongs to its best hotspot,
  // the summary has a row per hotspot and the sites table lists them hotspot by hotspot
  const hotspotOfSite = useMemo(() => {
    const m = new Map();
    mapHotspots.forEach((h) => h.sites.forEach((s) => { if (!m.has(s.id)) m.set(s.id, h); }));
    return m;
  }, [mapHotspots]);
  const hotspotSummary = useMemo(() => (isNewProject ? mapHotspots.map((h) => {
    const inside = (s) => {
      const lo = s.mpStart;
      const hi = s.mpEnd ?? s.mpStart;
      return Math.max(0, Math.min(hi, h.mpEnd) - Math.max(lo, h.mpStart));
    };
    const byAnalysis = {};
    let deficientMi = 0;
    h.sites.forEach((s) => {
      const a = byAnalysis[s.analysis] || (byAnalysis[s.analysis] = { sites: 0, miles: 0 });
      a.sites += 1;
      a.miles += inside(s);
      deficientMi += inside(s);
    });
    const road = h.segment ? `${h.road} · ${h.segment}` : h.road;
    return { key: h.id, rankLabel: h.label, label: `${road} · MP ${h.mpStart.toFixed(2)}–${h.mpEnd.toFixed(2)}`, road: h.road, segment: h.segment,
      ranges: [[h.mpStart, h.mpEnd]], miles: h.lengthMi, byAnalysis, sites: h.n, deficientMi, severe: h.severe,
      pct: h.lengthMi ? Math.min(100, (deficientMi / h.lengthMi) * 100) : 0, firstSite: h };
  }) : null), [isNewProject, mapHotspots]);
  const hotspotSummaryRank = useMemo(() => new Map(mapHotspots.map((h) => [h.id, h.rank])), [mapHotspots]);
  // the map in New project spawning: the hotspots and the sites inside the ones it shows
  const hotspotSiteIds = useMemo(() => new Set(mapHotspots.flatMap((h) => h.sites.map((s) => s.id))), [mapHotspots]);
  const shownMapSites = isNewProject ? sites.filter((s) => hotspotSiteIds.has(s.id)) : mapSites;
  // the map draws only what relates to the sites shown (every mode): their roads' analysis paths, their ISD maneuvers' rays
  const pathKeys = useMemo(() => new Set(shownMapSites.flatMap((s) => (s.directions || [s.direction]).map((d) => `${s.segKey}|${d}`))), [shownMapSites]);
  // the panel beside the map counts what is chosen: the milepost limits, and in a customer mode the sites on the map and their roads
  const stats = useMemo(() => {
    const clipped = range ? shownSegments.map((s) => clipSegment(s, range)) : shownSegments;
    if (full) return overviewStats(clipped, sites, allClusters, filters.direction);
    const on = new Set(shownMapSites.map((s) => s.segKey));
    return overviewStats(on.size ? clipped.filter((s) => on.has(s.key)) : clipped, shownMapSites, [], filters.direction);
  }, [full, range, shownSegments, sites, shownMapSites, allClusters, filters.direction]);
  // the score tables (how a score was reached): the crash points each site has once crash data is loaded
  const [scoreItem, setScoreItem] = useState(null);
  const scoreCrash = useCallback((s) => {
    let cost = 0;
    let correlated = 0;
    (crashes || []).forEach((c) => {
      if (c.segKey === s.segKey && c.milepost >= s.mpStart - NEAR_MI && c.milepost <= (s.mpEnd ?? s.mpStart) + NEAR_MI) {
        cost += costs[kabcoOf(c)] || 0;
        if ((c.findings || {})[s.analysis]) correlated += 1;
      }
    });
    return { cost, correlated };
  }, [crashes, costs]);
  // the PDF's appendix: the weights, a sample calculation per analysis, the first stretch's score site by site
  const pdfScoring = () => {
    const shownIds = new Set(shownMapSites.map((s) => s.id));
    const roadOf = (s) => `${s.segment ? `${s.road} · ${s.segment}` : s.road} ${(s.directions || [s.direction]).join(' ')} · MP ${(s.mpStart ?? 0).toFixed(2)}`;
    const examples = analyses.map((a) => {
      const s = (perAnalysis.byAnalysis[a] || []).find((x) => shownIds.has(x.id));
      if (!s) return null;
      const parts = siteScoreParts(s, scoreCrash(s), weights);
      return { analysis: a, site: roadOf(s), measured: s.worstText || '', severity: s.severity, severityPts: parts.severity, analysisPts: parts.analysis,
        crashPts: parts.crashCost + parts.correlated, total: parts.total };
    }).filter(Boolean);
    const first = isNewProject ? mapHotspots[0] : null;
    const stretch = first ? stretchBreakdown(first, weights) : null;
    return {
      logic: siteLogic(weights, Boolean(crashes)).replace(' Set the weights in Settings → Worst offenders.', ''),
      examples,
      stretch: stretch ? {
        title: `${first.label} · ${first.segment ? `${first.road} · ${first.segment}` : first.road} · MP ${first.mpStart.toFixed(2)}–${first.mpEnd.toFixed(2)}: its score, site by site. A site counts by the share of it inside the project limits.`,
        total: stretch.total,
        rows: stretch.rows.map((r) => ({ analysis: r.site.analysis, site: roadOf(r.site), severity: r.site.severity, severityPts: r.severity, analysisPts: r.analysis, share: r.share, points: r.points })),
      } : null,
    };
  };
  // ISD on the map follows the severity filter maneuver by maneuver: a maneuver's severity is its sight-distance
  // quality graded by the criteria (a maneuver that meets the required distance has none), and the Top N: only the
  // maneuvers of the ISD sites the map shows — their rays, and (RDV) the intersections holding them.
  const maneuverSeverity = useCallback((m) => (m.quality == null || m.quality >= 1 ? null
    : classifyValue('ISD', m.quality * 100, severity.thresholds)), [severity]);
  const keptManeuver = useCallback((m) => filters.severities.includes(maneuverSeverity(m)), [filters.severities, maneuverSeverity]);
  const shownManeuvers = useMemo(() => new Set(shownMapSites.filter((s) => s.analysis === 'ISD')
    .flatMap((s) => (s.maneuvers || []).filter(keptManeuver).map((m) => m.id))), [shownMapSites, keptManeuver]);
  const mapIntersections = useMemo(() => intersections.filter((ix) => (ix.maneuvers || []).some((m) => shownManeuvers.has(m.id)))
    .map((ix) => ({ ...ix, maneuvers: ix.maneuvers.filter(keptManeuver) })), [intersections, keptManeuver, shownManeuvers]);
  // the ISD sites on the map list (and link) only the maneuvers that meet the severity filter
  const mapSiteList = useMemo(() => shownMapSites.map((s) => (s.analysis === 'ISD' && s.maneuvers
    ? { ...s, maneuvers: s.maneuvers.filter(keptManeuver) } : s)), [shownMapSites, keptManeuver]);
  const customerStationLayers = useMemo(() => (shownStationLayers
    ? shownStationLayers.map((l) => {
      if (l.analysis === 'ISD') return { ...l, onlyManeuvers: shownManeuvers };
      if (l.analysis !== 'SSD' || (full && !mapTop.sites)) return l;
      // SSD: the stations and rays of the sites the map shows
      return { ...l, chunks: l.chunks.map((c) => ({ ...c, onlyRuns: shownMapSites
        .filter((s) => s.analysis === 'SSD' && s.segKey === c.segKey && s.direction === c.direction && s.mpStart != null)
        .map((s) => [s.mpStart, s.mpEnd ?? s.mpStart]) })) };
    })
    : shownStationLayers), [shownStationLayers, shownManeuvers, shownMapSites, full, mapTop.sites]);
  // rehab: the project segment's sites outside the project limits, faded on the map
  const fadedSites = useMemo(() => (mode === 'rehab' && range
    ? baseSites.filter((s) => s.mpStart != null && !((s.mpEnd ?? s.mpStart) >= range[0] && s.mpStart <= range[1])) : []),
  [mode, range, baseSites]);
  const sitesNav = { value: mapTop.sites, onChange: (n) => setMapTop({ sites: n }), rank: siteRank,
      max: byAnalysisRank ? Math.max(0, ...Object.values(perAnalysis.byAnalysis).map((l) => l.length)) : rankedSites.length,
      list: mapSites, pinned: pins.sites || [], rankBy, onRankBy: setRankBy, analyses, presets: full ? undefined : CUSTOMER_PRESETS,
      title: byAnalysisRank ? `${siteLogic(weights, Boolean(crashes))} Ranked per analysis: the Top N of each analysis.` : siteLogic(weights, Boolean(crashes)) };
  const topNav = {
    sites: isNewProject ? null : sitesNav,
    hotspots: isNewProject ? { value: mapTop.hotspots || 0, onChange: (n) => setMapTop({ hotspots: n }), max: rankedHotspots.length,
      rank: hotspotRank, list: mapHotspots, pinned: [], presets: [3, 5, 10, 25], prefix: hsPrefix,
      label: byLength ? 'Prospects' : 'Hotspots',
      title: byLength ? `The best ${hotspotCfg.lengthMi} mi stretches, by the score of what lies inside them`
        : "Hotspots ranked by the sum of their sites' scores (severity and analysis weights), worst first" } : null,
    // clusters: the RDV mode only
    clusters: full ? { value: mapTop.clusters, onChange: (n) => setMapTop({ clusters: n }), max: rankedClusters.length, rank: clusterRank,
      list: mapClusters, pinned: pins.clusters || [], title: clusterLogic(weights, Boolean(crashes)) } : null,
  };
  // the sites in rank order (grouped by analysis when ranked per analysis): the PDF follows it
  const siteOrderIds = byAnalysisRank
    ? Array.from(perAnalysis.order.entries()).sort((a, b) => a[1] - b[1]).map(([id]) => id)
    : rankedSites.map((x) => x.id);
  // the PDF's Score columns: the page's own scores, in the order of siteOrderIds
  const pdfSiteScores = useMemo(() => {
    if (dialog !== 'pdf') return [];
    const byId = new Map(sites.map((s) => [s.id, s]));
    return siteOrderIds.map((id) => (byId.has(id) ? Math.round(siteScoreParts(byId.get(id), scoreCrash(byId.get(id)), weights).total * 100) / 100 : null));
  }, [dialog, sites, weights, scoreCrash]); // eslint-disable-line react-hooks/exhaustive-deps
  // the customer report's sites: as many as the table shows (the Top N, of each analysis when ranked so), pinned first
  const pdfSiteIds = useMemo(() => {
    const n = full ? 0 : cview.rowsSites;
    const byId = new Map(sites.map((s) => [s.id, s]));
    const pinned = (pins.sites || []).filter((id) => byId.has(id));
    const per = {};
    const order = isNewProject
      ? [...siteOrderIds].filter((id) => hotspotOfSite.has(id)).sort((a, b) => hotspotOfSite.get(a).rank - hotspotOfSite.get(b).rank)
      : siteOrderIds;
    const head = order.filter((id) => byId.has(id) && !pinned.includes(id)).filter((id) => {
      if (!n) return true;
      const k = isNewProject ? hotspotOfSite.get(id).label : byAnalysisRank ? byId.get(id).analysis : '*';
      per[k] = (per[k] || 0) + 1;
      return per[k] <= n;
    });
    return [...pinned, ...head];
  }, [full, cview.rowsSites, sites, pins.sites, siteOrderIds, byAnalysisRank, isNewProject, hotspotOfSite]); // eslint-disable-line react-hooks/exhaustive-deps
  const chooseMode = (key) => {
    telemetryTrack('click', 'start.mode_pick', key);
    rememberMode(key);
    setMode(key);
    setAtStart(false);
    setRankByState(loadRankBy(key));
    setMapTopState(readMapTop(key));
    setBaseLayer(modeDefaults(key).baseLayer);
    setMpRange(null);
    setPickedHotspot(null);
    if (key === 'maintenance') setMaintTask(null);                 // Maintenance asks for the work first
    setFilters((f) => ({ ...f, analyses: null, segments: null, severities: [...(modeDefaults(key).severities || DEFAULT_SEVERITIES)],
      direction: isFull(key) ? f.direction : '' }));
    if (!isFull(key)) {
      setCrashOn(false);
      setDialog(null);
      setMenuOpen(false);
      setCview({ rowsSites: modeDefaults(key).rows, rowsSummary: modeDefaults(key).rows, rowsHotspots: modeDefaults(key).hotspotsTop || 5,
        showPaths: key === 'rehab' });
    }
  };
  const chooseTask = (t) => { setMaintTask(t); setFilters((f) => ({ ...f, analyses: null, severities: [...maintenanceSeverities(t)] })); };
  const rehabColors = useMemo(() => Object.fromEntries(Object.keys(roadColors).map((r) => [r, REHAB_PATH_COLOR])), [roadColors]);

  // ---- right of way (2026-10-25): a map layer, off by default; fetched for the roads shown once it is on
  const [rowOn, setRowOn] = useState(false);
  const [rowData, setRowData] = useState({});          // `${state}|${bbox}` -> {features, source, error}
  const rowBoxes = useMemo(() => {
    const byState = {};
    shownSegments.forEach((s) => Object.values(s.directions || {}).forEach((d) => (d.points || []).forEach((p) => {
      if (p[0] == null) return;
      const b = byState[s.state] || (byState[s.state] = [p[1], p[0], p[1], p[0]]);
      b[0] = Math.min(b[0], p[1]); b[1] = Math.min(b[1], p[0]); b[2] = Math.max(b[2], p[1]); b[3] = Math.max(b[3], p[0]);
    })));
    // a margin of ~400 m around the roads
    return Object.entries(byState).map(([st, b]) => [st, [b[0] - 0.005, b[1] - 0.004, b[2] + 0.005, b[3] + 0.004]]);
  }, [shownSegments]);
  useEffect(() => {
    if (!rowOn) return undefined;
    let live = true;
    rowBoxes.forEach(([st, b]) => {
      const k = `${st}|${b.map((v) => v.toFixed(3)).join(',')}`;
      if (rowData[k]) return;
      setRowData((m) => ({ ...m, [k]: { features: [], loading: true } }));
      fetchRightOfWay(st, b).then((r) => { if (live) setRowData((m) => ({ ...m, [k]: { features: r.features || [], source: r.source, error: r.error } })); })
        .catch((e) => { if (live) setRowData((m) => ({ ...m, [k]: { features: [], error: e.message } })); });
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowOn, rowBoxes]);
  const rowLayer = useMemo(() => {
    const wanted = rowBoxes.map(([st, b]) => `${st}|${b.map((v) => v.toFixed(3)).join(',')}`);
    const parts = wanted.map((k) => rowData[k]).filter(Boolean);
    return { key: wanted.join(';') + parts.map((x) => x.features.length).join(','),
      features: parts.flatMap((x) => x.features.map((f) => ({ ...f, properties: { ...f.properties, source: x.source } }))),
      source: (parts.find((x) => x.source) || {}).source || null, loading: parts.some((x) => x.loading),
      error: (parts.find((x) => x.error) || {}).error || '' };
  }, [rowBoxes, rowData]);

  // ---- site notes (2026-10-25): everyone with access to a road sees and edits them
  const [notesBySeg, setNotesBySeg] = useState({});
  const [noteSite, setNoteSite] = useState(null);
  const segListKey = segments.map((s) => s.key).join(',');
  useEffect(() => {
    if (!mode) return undefined;
    let live = true;
    const missing = segments.filter((s) => !(s.key in notesBySeg));
    if (!missing.length) return undefined;
    mapLimit(missing, 4, async (s) => {
      let list = [];
      try { list = await fetchSiteNotes(s); } catch (e) { /* the page works without notes */ }
      if (live) setNotesBySeg((m) => ({ ...m, [s.key]: list }));
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segListKey, Boolean(mode)]);
  useEffect(() => { setNotesBySeg({}); }, [sourceKey]);
  const siteNotesOf = useMemo(() => matchNotes(baseSites, notesBySeg), [baseSites, notesBySeg]);
  const saveNote = async (site, status, text) => {
    const seg = segByKey.get(site.segKey);
    const current = siteNotesOf[site.id] || null;
    const row = await saveSiteNote(seg, site, current, status, text);
    setNotesBySeg((m) => ({ ...m, [site.segKey]: [...(m[site.segKey] || []).filter((n) => !current || n.id !== current.id),
      { ...row, versions: ((current && current.versions) || 0) + 1 }] }));
  };
  // the notes the PDF carries: the sites shown, with their status in words
  const pdfNotes = useMemo(() => Object.fromEntries(sites.filter((s) => siteNotesOf[s.id]).map((s) => {
    const n = siteNotesOf[s.id];
    return [s.id, { status: n.status, statusLabel: STATUS_LABEL[n.status] || n.status, note: n.note, author: n.author, at: n.at }];
  })), [sites, siteNotesOf]);
  const shownStates = useMemo(() => Array.from(new Set(shownSegments.map((s) => s.state))), [shownSegments]);
  const [ixState, setIxState] = useState('');
  const intersectionState = shownStates.includes(ixState) ? ixState : shownStates[0] || '';

  // the Safety panels (Insights, Cluster insights, Program, Intersections) speak the Safety
  // Dashboard's focus: {crashId} | {bounds, lat, lng} | {mpStart, analysis, direction, corridor} | {lat, lng}
  const focusFromPanel = (f, opts) => {
    if (!f) return;
    if (f.crashId != null) {
      const c = (crashes || []).find((x) => x.id === f.crashId);
      showOnMap(c || { id: f.crashId, lat: f.lat, lng: f.lng }, opts);
      return;
    }
    if (f.lat == null && f.mpStart != null) {
      showOnMap(sites.find((s) => s.segKey === f.corridor && s.analysis === f.analysis && s.direction === f.direction
        && s.mpStart === f.mpStart), opts);
      return;
    }
    showOnMap({ id: f.key, lat: f.lat, lng: f.lng }, opts);
  };

  // opts.scroll === false: move the map to the item without bringing the map into view
  const showOnMap = useCallback((item, opts) => {
    if (!item || item.lat == null) return;
    // The site id is app data, not anything the user typed: it is what makes the
    // geographic heatmap (plan §6) possible later.
    telemetryTrack('map', 'map.site_click', item.id);
    setFocus({ id: item.id, lat: item.lat, lng: item.lng, bounds: boundsOf(item), stamp: Date.now() });
    if (mapRef.current && !(opts && opts.scroll === false)) mapRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const patchView = (p) => setView((v) => ({ ...v, ...p }));
  const loading = progress.phase === 'corridors' || progress.phase === 'segments';
  // what is still on its way, said on the map itself
  const mapLoading = progress.phase === 'segments' ? `Loading the roads: ${progress.done} of ${progress.total}`
    : rulesLoad.total > 0 ? `Applying the rules: ${rulesLoad.done} of ${rulesLoad.total} roads`
      : stationLoad.total > 0 ? `Loading the stations: ${stationLoad.done} of ${stationLoad.total}`
        : crashLoad.total > 0 ? `Loading the crash data: ${crashLoad.done} of ${crashLoad.total}`
          : rowLayer && rowLayer.loading ? 'Loading the right of way' : null;
  const setCorrelationRules = (next) => { saveDisabledRules(next); setDisabledRules(next); };
  const maxBar = Math.max(1, ...stats.byAnalysis.map((b) => b.sites));

  // --- product analytics (RDV-only reporting) ---------------------------------
  // Capture lives and dies with this page: init on mount, stop on unmount, so
  // nothing elsewhere in the platform is recorded (telemetry plan §1). init()
  // returns its own stop function, which is exactly the cleanup we want.
  useEffect(() => telemetryInit({ app: 'safety_dashboard' }), []);
  useTelemetryContext(atStart ? 'start' : (mode || null), sourceKey || null);
  const mapDwellRef = usePanelDwell('panel.map');

  // One flat snapshot of everything that shapes what the user is looking at. The
  // keys ARE the catalog targets, so a change emits a named `filter` event and
  // the whole combination is hashed onto every later event — which is how "what
  // filters were set at the time" is answerable without storing the blob twice.
  useTrackedSettings(useMemo(() => ({
    'filter.analyses': filters.analyses,
    'filter.segments': filters.segments,
    'filter.severities': filters.severities,
    'filter.direction': filters.direction,
    'filter.mp_range': range ? [Number(range[0].toFixed(2)), Number(range[1].toFixed(2))] : null,
    'filter.obstructions': rules.obstructions,
    'filter.right_of_way': rowFilter,
    'filter.curves_by_direction': curvesByDirection,
    'filter.net_types': netTypes,
    'filter.crash': crashOn ? crashFilters : null,
    'filter.rules': appliedRules,
    'filter.weights': weights,
    'filter.severity_settings': severity,
    'filter.hotspots': hotspotCfg,
    'filter.rank_by': rankBy,
    'filter.map_top': mapTop,
    'filter.rows': cview,
    'filter.maintenance_task': maintTask,
    'filter.project_segment': rehabSeg,
    'filter.crash_analysis': crashAnalysis,
    'filter.correlation_rules': disabledRules,
    'filter.verdict_show': verdictShow,
    'filter.unit_costs': unitCostOverrides,
    'filter.intersection_state': intersectionState,
    'view.unify': view.unify,
    'view.axis': view.axis,
    'view.colour_by': view.colourBy,
    'view.sum_by': view.sumBy,
    'view.measure': view.measure,
    'view.show_paths': view.showPaths,
    'view.base_layer': baseLayer,
  }), [filters, range, rules.obstructions, rowFilter, curvesByDirection, netTypes,
    crashOn, crashFilters, appliedRules, weights, severity, hotspotCfg, rankBy,
    mapTop, cview, view, baseLayer, maintTask, rehabSeg, crashAnalysis,
    disabledRules, verdictShow, unitCostOverrides, intersectionState]));

  // Dialogs: `dialog` carries new/edit/pdf/criteria/offenders; the rest have
  // their own state. Only the open is recorded — a close tells us nothing extra.
  useEffect(() => {
    const names = {
      new: 'dialog.new_overview', edit: 'dialog.edit_overview', pdf: 'dialog.pdf',
      criteria: 'dialog.criteria', offenders: 'dialog.offenders',
    };
    if (dialog && names[dialog]) telemetryTrack('dialog', names[dialog], 'open');
  }, [dialog]);
  useEffect(() => { if (rulesOpen) telemetryTrack('dialog', 'dialog.settings', 'open'); }, [rulesOpen]);
  useEffect(() => { if (scoreItem) telemetryTrack('dialog', 'dialog.score', 'open'); }, [scoreItem]);
  useEffect(() => { if (noteSite) telemetryTrack('dialog', 'dialog.site_note', 'open'); }, [noteSite]);
  useEffect(() => { if (deleting) telemetryTrack('dialog', 'dialog.road_element_delete', 'open'); }, [deleting]);
  useEffect(() => { if (pickedHotspot) telemetryTrack('map', 'map.hotspot_pick'); }, [pickedHotspot]);
  const raysSeen = useRef(null);
  useEffect(() => {
    const prev = raysSeen.current;
    raysSeen.current = hiddenRays;
    if (prev !== null && prev.length !== hiddenRays.length) {
      telemetryTrack('map', 'map.ray_toggle', hiddenRays.length ? `${hiddenRays.length} hidden` : 'all shown');
    }
  }, [hiddenRays]);
  // Every table's sort through one diff, rather than five inline handlers: the
  // value is which table was re-sorted and on what.
  const sortsSeen = useRef(null);
  useEffect(() => {
    const prev = sortsSeen.current;
    sortsSeen.current = sorts;
    if (!prev) return;
    Object.keys(sorts).forEach((table) => {
      if (JSON.stringify(prev[table]) !== JSON.stringify(sorts[table])) {
        telemetryTrack('click', 'table.sort', `${table}:${sorts[table]?.key}:${sorts[table]?.dir}`);
      }
    });
  }, [sorts]);
  // Skip the mount pass: the crash layer starts off, and that is not a toggle.
  const crashToggleSeen = useRef(false);
  useEffect(() => {
    if (!crashToggleSeen.current) { crashToggleSeen.current = true; return; }
    telemetryTrack('map', 'map.crash_toggle', crashOn ? 'on' : 'off');
  }, [crashOn]);
  // Every overlay in the map's layer control — Crashes, Road network, Right of
  // way, Mileposts, Site notes, the station groups, all of them — by name, as
  // the user switches it. One mechanism rather than a watcher per layer, so a
  // layer added later is logged without anyone remembering to wire it.
  const onMapLayer = useCallback((name, on) => {
    telemetryTrack('map', 'map.layer', `${name} ${on ? 'on' : 'off'}`);
  }, []);

  // Zoom and pan. Leaflet fires these continuously while the user drags, so they
  // are coalesced: one event per settled viewport, and a pan that did not move
  // far enough to matter is dropped. The rounded centre is what the geographic
  // heatmap will bin on later.
  const lastViewport = useRef(null);
  const viewportTimer = useRef(null);
  const onMapViewport = useCallback((v) => {
    if (viewportTimer.current) clearTimeout(viewportTimer.current);
    viewportTimer.current = setTimeout(() => {
      const prev = lastViewport.current;
      const lat = Math.round(v.center.lat * 1e4) / 1e4;
      const lng = Math.round(v.center.lng * 1e4) / 1e4;
      if (prev && prev.zoom !== v.zoom) {
        telemetryTrack('map', 'map.zoom', `${prev.zoom} → ${v.zoom}`);
      } else if (prev) {
        const moved = Math.abs(prev.lat - lat) + Math.abs(prev.lng - lng);
        if (moved > 0.0005) telemetryTrack('map', 'map.pan', `${lat},${lng} z${v.zoom}`);
      }
      lastViewport.current = { zoom: v.zoom, lat, lng };
    }, 600);
  }, []);
  useEffect(() => () => { if (viewportTimer.current) clearTimeout(viewportTimer.current); }, []);

  // Beside the map: the panels must stay inside the map's height. Measure after every change
  // of what they show and, if they spill over, step up: 1 compact type, 2 no optional sub
  // lines, 3 no sub lines at all (the numbers always stay).
  const fitKey = [crashStats ? 1 : 0, economics ? 1 : 0, analyses.join(','), stats.sites, crashStats?.nCrashes].join('|');
  useLayoutEffect(() => { setFitLevel(0); }, [fitKey]);
  useLayoutEffect(() => {
    const el = statsRef.current;
    if (!el || fitLevel >= 3) return;
    if (el.scrollHeight > el.clientHeight + 1) setFitLevel((n) => n + 1);
  });

  if (overviews === null) return <div className="safety-dashboard ov-dashboard"><div className="safety-placeholder">Loading…</div></div>;

  return (
    <div className="safety-dashboard ov-dashboard so-dashboard"
      style={{ '--sev-severe': severity.colors.severe, '--sev-moderate': severity.colors.moderate, '--sev-minor': severity.colors.minor }}>
      <header className="safety-header">
        <div>
          <h1>
            Safety Dashboard
            {mode && !atStart && (
              <span className="so-mode-title"> · {full ? <><RdvMark size={20} /> Full capabilities</> : modeOf(mode).label}</span>
            )}
          </h1>
          <p className="safety-muted">
            {atStart ? 'Choose the data, then how to look at it.'
              : full ? 'Every road of an order or of a state on one map, with its deficiencies, clusters and reports.'
                : 'The deficiencies RDV\'s analyses found on your roads, worst first.'}
          </p>
        </div>
        {!atStart && (
        <div className="ov-header-tools">
          <ModeSwitch mode={mode} canFull={canFull} onChange={chooseMode} onStart={() => setAtStart(true)} />
          <label className="safety-filter" htmlFor="so-pick"><span>Show</span>
            <select id="so-pick" value={sourceKey || ''} onChange={(e) => setSourceKey(e.target.value || null)}
              disabled={!overviews.length && !states.length}>
              {!overviews.length && !states.length && <option value="">Nothing to show yet</option>}
              {overviews.length > 0 && (
                <optgroup label="Overviews">
                  {overviews.map((o) => <option key={o.id} value={sourceId('ov', o.id)}>{o.name} ({o.projects.length} project{o.projects.length === 1 ? '' : 's'})</option>)}
                </optgroup>
              )}
              {canAllRoads && states.length > 0 && (
                <optgroup label="Whole states">
                  {states.map((x) => <option key={x.id} value={sourceId('st', x.id)}>All collections in {x.label}</option>)}
                </optgroup>
              )}
            </select>
          </label>
          {full && (
          <>
          {overview?.canEdit && <button type="button" className="safety-btn safety-btn-ghost" onClick={() => setDialog('edit')}>Edit</button>}
          {overview?.canEdit && <button type="button" className="safety-btn safety-btn-ghost" onClick={deleteThisOverview}>Delete</button>}
          {canCreate && <button type="button" className="safety-btn safety-btn-ghost" onClick={() => setDialog('new')}>New overview</button>}
          <button type="button" className={`safety-btn ${crashOn ? 'safety-btn-ghost' : ''}`} disabled={!segments.length}
            onClick={() => setCrashOn((on) => !on)} aria-pressed={crashOn}
            title={crashOn ? 'Back to the deficiencies only' : 'Load the crash data of the roads shown: crash layers, filters, correlation'}>
            {crashOn ? 'Unload crash data' : 'Load crash data'}
          </button>
          <div className="so-settings" ref={menuRef}>
            <button type="button" className="safety-btn safety-btn-ghost so-gear" aria-haspopup="menu" aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)} title="Criteria and severity, correlation rules, filters">
              <i className="fa-solid fa-gear" aria-hidden="true" /> Settings
              {disabledRules.length > 0 && <span className="so-badge" title={`${disabledRules.length} correlation rule(s) off`}>{disabledRules.length}</span>}
            </button>
            {menuOpen && (
              <div className="so-settings-menu" role="menu">
                <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setDialog('criteria'); }}>
                  <span>Criteria and severity</span><small>what counts as a deficiency, its severe / moderate cuts and colours</small>
                </button>
                <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setRulesOpen(true); }}>
                  <span>Correlation rules</span>
                  <small>how a crash is tied to each analysis · {CORRELATION_RULES.length - disabledRules.length} of {CORRELATION_RULES.length} on</small>
                </button>
                <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setDialog('filters'); }}>
                  <span>Filters</span><small>what counts as a deficiency: run lengths, guardrail gaps, passing-zone rules</small>
                </button>
                <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setDialog('offenders'); }}>
                  <span>Worst offenders</span><small>how the insights rank crashes and clusters</small>
                </button>
              </div>
            )}
          </div>
          <button type="button" className="safety-btn ov-primary" disabled={!overview || loading || !segments.length}
            hidden={!overview}
            onClick={() => setDialog('pdf')} title={`Export the ${REPORT_NAME} as a PDF`}>Export PDF report</button>
          </>
          )}
          {!full && overview && (
            <button type="button" className="safety-btn ov-primary" disabled={loading || !segments.length}
              onClick={() => setDialog('pdf')} title="Export a PDF report of what is shown">Export PDF report</button>
          )}
        </div>
        )}
      </header>

      {atStart && (
        <StartScreen overviews={overviews} states={states} sourceKey={sourceKey} onSource={setSourceKey}
          canFull={canFull} canAllRoads={canAllRoads} lastMode={recallMode()} onChoose={chooseMode} />
      )}

      {listError && <div className="safety-error">Could not load the overviews: {listError}</div>}
      {!overviews.length && !states.length && !listError && (
        <div className="safety-placeholder">
          {canCreate
            ? 'No overview yet. Create one from the RSA projects of an order with "New overview".'
            : 'No overview includes a project you are assigned to yet. An administrator or your Super User can create one.'}
        </div>
      )}

      {!atStart && active && (
        <>
          {overview?.description && <p className="safety-muted ov-description">{overview.description}</p>}
          {(loading || progress.failed.length > 0) && (
            <div className="safety-note" role="status">
              {progress.phase === 'corridors' && (overview
                ? `Finding the analysed roads of ${overview.projects.length} project${overview.projects.length === 1 ? '' : 's'}…`
                : `Finding the analysed roads of ${stateSource.label}…`)}
              {progress.phase === 'segments' && `Loading ${progress.done} of ${progress.total} segments… The first load of a road reads its analyses and can take a minute; later loads are instant.`}
              {progress.phase === 'ready' && progress.failed.length > 0 && `Could not load: ${progress.failed.join(', ')}.`}
            </div>
          )}
          {full && pendingNet && (
            <div className="safety-note" role="status">
              Reading the road network (MassDOT, OpenStreetMap) for {pendingNet.split(',').length} road{pendingNet.split(',').length === 1 ? '' : 's'} in
              the background. The passing-zone rules near signals, intersections, crosswalks and the other SOP features
              apply to {pendingNet.split(',').length === 1 ? 'it' : 'them'} as soon as it arrives; everything else is already shown.
            </div>
          )}
          {stationLoad.total > 0 && (
            <div className="safety-note" role="status">
              Loading the {stationAnalyses.join(', ')} stations: {stationLoad.done} of {stationLoad.total} done.
              The first load reads every station of a road and can take a minute.
            </div>
          )}
          {rulesLoad.total > 0 && (
            <div className="safety-note" role="status">
              Applying the new rules: {rulesLoad.done} of {rulesLoad.total} roads. The previous results stay on screen until then.
            </div>
          )}
          {crashOn && crashLoad.total > 0 && (
            <div className="safety-note" role="status">
              Loading crash data: {crashLoad.done} of {crashLoad.total} roads. The first load of a road matches its crashes to
              every deficiency and can take half a minute; later loads are instant.
            </div>
          )}
          {crashOn && crashFailed.length > 0 && (
            <div className="safety-note" role="status">No crash data for: {crashFailed.join(', ')}.</div>
          )}
          {progress.phase === 'ready' && !segments.length && (
            <div className="safety-placeholder">No analysed road with a surveyed centreline was found {overview ? 'in these projects' : 'in this state'}.</div>
          )}

          {mode === 'maintenance' && !maintTask && <MaintenanceTasks task={loadPref('lastMaintTask', null)} onChoose={(t) => { savePref('lastMaintTask', t); chooseTask(t); }} />}

          {segments.length > 0 && !(mode === 'maintenance' && !maintTask) && (
            <>
              {mode === 'maintenance' && (
                <MaintenanceBar task={maintTask} onTask={(t) => { savePref('lastMaintTask', t); chooseTask(t); }} />
              )}
              <div className="so-filters">
              <button type="button" className="so-filters-head" aria-expanded={filtersOpen} onClick={() => setFiltersOpen((o) => !o)}
                title={filtersOpen ? 'Collapse the filters' : 'Show the filters'}>
                <span className={`safety-section-chevron ${filtersOpen ? 'open' : ''}`}>▸</span>
                <span className="so-filters-title">Filters</span>
                {!filtersOpen && (
                  <span className="so-filters-summary">
                    {analyses.length} of {available.length} analyses · {segKeys.length} of {segments.length} segments
                    {' · '}{filters.severities.length === SEVERITIES.length ? 'every severity' : filters.severities.join(' + ')}
                    {full && <>{' · '}{filters.direction === 'P' ? 'positive direction' : filters.direction === 'N' ? 'negative direction' : 'both directions'}</>}
                    {crashOn ? ` · crashes: ${crashFilters.severities.length ? crashFilters.severities.join(' + ') : 'every severity'}${crashFilters.correlatedOnly ? ', correlated only' : ''}` : ''}
                    {range ? ` · MP ${range[0].toFixed(2)}–${range[1].toFixed(2)}` : ''}
                  </span>
                )}
              </button>
              {filtersOpen && (
              <>
              <div className="safety-filterbar">
                <MultiPicker tall isolate label="Analyses" noun="analyses"
                  options={available.map((a) => ({ key: a, label: `${a} · ${ANALYSIS_NAMES[a]}${analysisCounts[a] ? '' : ' · none here'}`, empty: !analysisCounts[a] }))}
                  selected={analyses} onChange={(next) => setFilters((f) => ({ ...f, analyses: next }))}
                  extraToggle={full ? { id: 'ov-show-paths', label: 'Show the analysis paths on the map', checked: view.showPaths, onChange: (v) => patchView({ showPaths: v }) } : undefined}
                  title="Up to three analyses are drawn with their own report signs; four or more as coloured runs" />
                {mode === 'rehab' ? (
                  <ProjectSegmentPicker segments={segments} value={rehabSeg} nameOf={segmentName}
                    onChange={(k) => { setMpRange(null); setFilters((f) => ({ ...f, segments: [k] })); }} />
                ) : (
                <MultiPicker tall label="Roads and segments" noun="segments" wide
                  options={segments.map((s) => ({ key: s.key, label: `${segmentName(s)} · MP ${(s.mpMin || 0).toFixed(1)}–${(s.mpMax || 0).toFixed(1)} · ${s.projectId ? `#${s.projectId}` : s.route}` }))}
                  selected={segKeys} onChange={(next) => setFilters((f) => ({ ...f, segments: next }))} />
                )}
                <MultiPicker tall label="Severity" noun="severities" options={SEVERITIES.map((s) => ({ key: s, label: s }))}
                  selected={filters.severities} onChange={(next) => setFilters((f) => ({ ...f, severities: next }))} />
                {analyses.includes('HCA') && !filters.direction && (
                  <label className="safety-filter" htmlFor="so-hca-curves"
                    title="A horizontal curve is surveyed in both directions. One curve joins them under the worse direction; each direction lists them apart.">
                    <span>HCA curves</span>
                    <select id="so-hca-curves" value={curvesByDirection ? 'dir' : 'one'} onChange={(e) => setCurvesByDirection(e.target.value === 'dir')}>
                      <option value="one">One per curve</option>
                      <option value="dir">Each direction</option>
                    </select>
                  </label>
                )}
                {isNewProject && <HotspotSettings value={hotspotCfg} onChange={setHotspotCfg} />}
                <label className="safety-filter" htmlFor="so-row-filter"
                  title="How much of a site's sight line lies inside the state right of way (approximate). 100%: the state can fix it within its right of way. Sight-distance sites (SSD, ISD) carry the figure; the other analyses lie on the road and count as 100%.">
                  <span>Right of way</span>
                  <select id="so-row-filter" value={rowFilter} onChange={(e) => setRowFilter(e.target.value)}>
                    <option value="all">Every deficiency</option>
                    <option value="within">100% in right of way</option>
                    <option value="outside">Under 100% in right of way</option>
                  </select>
                </label>
                {full && obstructionOptions.length > 0 && (
                  <MultiPicker tall allMeansEmpty label="Obstruction" noun="obstructions" options={obstructionOptions}
                    selected={rules.obstructions} onChange={(next) => setRules((r) => ({ ...r, obstructions: next }))}
                    title="What blocks the sight line (SSD stations, ISD maneuvers). Narrowed: only those deficiencies stay, and the analyses without an obstruction (PSD, GLR, FSA, GRW, HCA) are hidden — map, tables, correlation, verdicts and the PDF alike." />
                )}
                {full && netTypeOptions.length > 0 && (
                  <MultiPicker tall allMeansEmpty label="Road features" noun="road features" options={netTypeOptions}
                    selected={netTypes} onChange={setNetTypes}
                    title="Which road features (SOP passing-zone conditions) the map shows. Display only: the passing-zone rules still use every feature." />
                )}
                {full && (
                <>
                <label className="safety-filter" htmlFor="ov-dir"><span>Direction</span>
                  <select id="ov-dir" value={filters.direction} onChange={(e) => setFilters((f) => ({ ...f, direction: e.target.value }))}>
                    <option value="">Both</option><option value="P">Positive</option><option value="N">Negative</option>
                  </select>
                </label>
                <div className="safety-filter"><span>Segments</span>
                  <Seg label="Segments" value={view.unify} onChange={(v) => patchView({ unify: v })}
                    options={[[false, 'Separately'], [true, 'Unified per road']]} />
                </div>
                <div className="safety-filter"><span>Position as</span>
                  <Seg label="Position as" value={view.axis} onChange={(v) => patchView({ axis: v })}
                    options={[['mp', 'Milepost'], ['mi', 'Mileage']]} />
                </div>
                <div className="safety-filter"><span>Colour sites by</span>
                  <Seg label="Colour sites by" value={view.colourBy} onChange={(v) => patchView({ colourBy: v })}
                    options={[['analysis', 'Analysis'], ['severity', 'Severity']]} />
                </div>
                </>
                )}
              </div>
              {crashOn && (
                <CrashFilters crashes={allCrashes} datasetYears={firstCrashPayload?.datasetYears} value={crashFilters}
                  onChange={setCrashFilters} disabledCount={disabledRules.length} hasVerdicts={hasVerdicts} />
              )}
              {(full || mode === 'rehab' || mode === 'newProject') && roadExtent && roadExtent[1] > roadExtent[0] && (
                <div className="safety-filterbar so-range" aria-label="Milepost range">
                  <span className="so-crash-label so-range-label">{mode === 'rehab' ? 'Project limits' : mode === 'newProject' ? 'Search area' : 'Mileposts'}</span>
                  <input type="range" aria-label="From milepost" min={roadExtent[0]} max={roadExtent[1]} step={0.01}
                    value={(range || roadExtent)[0]}
                    onChange={(e) => setRangeEnd(0, Number(e.target.value))} />
                  <input type="range" aria-label="To milepost" min={roadExtent[0]} max={roadExtent[1]} step={0.01}
                    value={(range || roadExtent)[1]}
                    onChange={(e) => setRangeEnd(1, Number(e.target.value))} />
                  <span className="ov-inline">MP
                    <MpInput label="From milepost" value={(range || roadExtent)[0]} min={roadExtent[0]}
                      max={Math.max(roadExtent[0], (range || roadExtent)[1] - MIN_RANGE_MI)} onChange={(lo) => setRangeEnd(0, lo)} />
                    –
                    <MpInput label="To milepost" value={(range || roadExtent)[1]}
                      min={Math.min(roadExtent[1], (range || roadExtent)[0] + MIN_RANGE_MI)} max={roadExtent[1]} onChange={(hi) => setRangeEnd(1, hi)} />
                  </span>
                  {range && <button type="button" className="safety-btn safety-btn-ghost" onClick={() => setMpRange(null)}>Whole road</button>}
                </div>
              )}
              </>
              )}
              </div>

              <div className="ov-main" ref={mapRef}>
                <div className="ov-map-pane" ref={mapDwellRef}>
                  {/* a customer mode: the deficiency sites only, on the Light gray map (remounted per mode: its base layer) */}
                  <OverviewMap key={full ? 'full' : 'customer'} defaultBase={modeDefaults(mode).baseLayer}
                    segments={mapSegments} sites={mapSiteList} clusters={full ? mapClusters : []} topNav={topNav}
                    hotspots={isNewProject ? mapHotspots : []} fadedSites={fadedSites}
                    notes={siteNotesOf} onNote={setNoteSite} row={rowLayer} onRowToggle={setRowOn} rowFlags={rowFlags}
                    intersections={full ? mapIntersections : []} analyses={analyses}
                    colourBy={full ? view.colourBy : 'analysis'} roadColors={mode === 'rehab' ? rehabColors : roadColors} focus={focus} details={details}
                    loadingText={mapLoading} stationLayers={customerStationLayers} pathsInControl={!full} pathKeys={pathKeys} stationsOn={full || CUSTOMER_STATIONS_ON} showClusters={full}
                    showPaths={full ? view.showPaths : cview.showPaths} direction={filters.direction}
                    crashes={crashes} crashClusters={crashClusters} crashAnalysis={crashAnalysis} recommendations={recommendations}
                    isd={full ? isdControl : null} places={full ? places : null} onAddPlace={full ? addPlace : null}
                    onUpdatePlace={updatePlace} onRemovePlace={removePlace}
                    network={full ? mapNetwork : null} onBaseLayer={setBaseLayer} onViewport={onMapViewport} onLayer={onMapLayer}
                    canDeleteNetwork={canDeleteNetwork} onDeleteNetwork={setDeleting} grwShield={!appliedRules.grwShield.ignoreInventory} />
                </div>
                {/* Right of the map: the panels that fit its (constant) height. With crash data the
                    crash panels take the place of "Densest road" and ISD gets its compact form. */}
                <div ref={statsRef} className={`ov-stats ${crashStats ? 'so-stats-crash' : ''} ${fitLevel ? `so-fit-${fitLevel}` : ''}`}>
                  <Tile label={full ? (filters.direction ? `Surveyed · ${filters.direction === 'P' ? 'positive' : 'negative'} direction` : 'Surveyed road')
                    : range ? `Road within MP ${range[0].toFixed(2)}–${range[1].toFixed(2)}` : 'Road shown'}
                    value={`${stats.miles.toFixed(1)} mi`}
                    opt sub={`${filters.direction ? '' : `${stats.directionalMi.toFixed(1)} mi counting both directions · `}${stats.segments} segment${stats.segments === 1 ? '' : 's'} · ${stats.roads} road${stats.roads === 1 ? '' : 's'}${overview ? ` · ${stats.projects} project${stats.projects === 1 ? '' : 's'}` : ''}`} />
                  <Tile label="Deficiency sites" value={stats.sites} sub={<><b>{stats.severe} severe</b> · {stats.moderate} moderate · {stats.minor} minor</>} />
                  {full && <Tile label="Deficient length" value={`${stats.deficientMi.toFixed(1)} mi`} opt sub={<><b>{stats.pct.toFixed(1)}%</b> of the {stats.directionalMi.toFixed(1)} directional miles analysed</>} />}
                  {full && <Tile label="Multi-deficiency clusters" value={stats.clusters} sub={`${stats.clusters3} with 3+ analyses`} />}
                  {crashStats && (
                    <>
                      <Tile label="Crashes" value={crashStats.nCrashes} opt
                        sub={<>{stats.miles ? `${(crashStats.nCrashes / stats.miles).toFixed(2)} per road mile · ` : ''}{firstCrashPayload?.datasetVersion || ''}</>} />
                      <Tile label="Fatal · injury · property damage"
                        value={`${crashStats.bySeverity.fatal} · ${crashStats.bySeverity.injury} · ${crashStats.bySeverity.pdo}`}
                        opt sub={crashStats.nAnimal ? `${crashStats.nAnimal} animal strike${crashStats.nAnimal === 1 ? '' : 's'}` : null} />
                      <Tile label={`Near a deficiency site (${NEAR_MI} mi)`} value={crashStats.nearShare != null ? `${crashStats.nearShare}%` : '—'}
                        sub={<><b>{crashStats.crashesNearRuns}</b> of {crashStats.nCrashes} crashes · {crashStats.crashesInsideRuns} inside a site</>} />
                      <Tile label="Crash clusters" value={crashClusters.length}
                        sub={`${crashClusters.filter((c) => !c.hit).length} with no deficiency site near`} />
                      {economics && (economics.benefitPerYr
                        ? <Tile wide label="Expected savings / yr" value={fmtUsd(economics.benefitPerYr)}
                          opt sub={`countermeasure program · ${policyLabel}`} />
                        : <Tile wide label="Social cost at deficiencies" value={fmtUsd(economics.cost)}
                          sub={`${economics.correlated} correlated crash${economics.correlated === 1 ? '' : 'es'}${economics.lives ? ` · ${economics.lives} li${economics.lives === 1 ? 'fe' : 'ves'}` : ''}`} />)}
                    </>
                  )}
                  {analyses.includes('ISD') && crashStats && (
                    <Tile wide label="ISD · intersections with a deficiency" value={`${stats.intersectionsShort} of ${stats.intersections}`}
                      sub={`${stats.maneuversShort} of ${stats.maneuvers} maneuvers short of the required sight distance`} />
                  )}
                  {analyses.includes('ISD') && !crashStats && (
                    <Tile wide label="ISD · intersections analysed" value={stats.intersections}
                      sub={stats.intersections
                        ? <><b>{stats.intersectionsShort} with a deficiency</b> · {stats.maneuvers} maneuver{stats.maneuvers === 1 ? '' : 's'} analysed, {stats.maneuversShort} short of the required sight distance</>
                        : 'No intersection analysis in this selection'} />
                  )}
                  {full && !crashStats && (
                    <Tile wide label="Densest road" value={stats.densest ? stats.densest.road : '—'}
                      sub={stats.densest ? <><b>{stats.densest.rate.toFixed(2)} sites per mile</b></> : null} />
                  )}
                  {!full && (
                    <Tile wide label="By analysis · sites">
                      <div className="ov-bars">
                        {analyses.map((a) => {
                          const n = shownMapSites.filter((s) => s.analysis === a).length;
                          return (
                            <div className={`ov-bar ${n ? '' : 'so-bar-empty'}`} key={a} title={ANALYSIS_NAMES[a]}>
                              <span>{a}</span>
                              <span className="ov-bar-track"><span className="ov-bar-fill" style={{ width: `${(n / Math.max(1, stats.sites)) * 100}%`, background: ANALYSIS_COLORS[a] }} /></span>
                              <span className="ov-bar-n">{n}</span>
                            </div>
                          );
                        })}
                        {!analyses.length && <span className="ov-tile-sub">No analysis selected</span>}
                      </div>
                    </Tile>
                  )}
                  {full && (
                  <Tile wide label={`By analysis · sites · deficient miles${analyses.includes('ISD') ? ' (ISD above)' : ''}`}>
                    <div className="ov-bars">
                      {stats.byAnalysis.filter((b) => analyses.includes(b.analysis)).map((b) => (
                        <div className="ov-bar" key={b.analysis} title={ANALYSIS_NAMES[b.analysis]}>
                          <span>{b.analysis}</span>
                          <span className="ov-bar-track"><span className="ov-bar-fill" style={{ width: `${(b.sites / maxBar) * 100}%`, background: ANALYSIS_COLORS[b.analysis] }} /></span>
                          <span className="ov-bar-n">{b.sites} · {b.miles.toFixed(1)} mi</span>
                        </div>
                      ))}
                      {!stats.byAnalysis.some((b) => analyses.includes(b.analysis)) && <span className="ov-tile-sub">{analyses.length ? 'ISD is reported above' : 'No analysis selected'}</span>}
                    </div>
                  </Tile>
                  )}
                </div>
              </div>

              {full && (
              <>
              <div className={`safety-insights-grid ${crashes ? '' : 'single'}`}>
                <SectionCard track="panel.cluster_insights" title="Cluster insights" meta={`${clusterCards.length} multi-deficiency cluster${clusterCards.length === 1 ? '' : 's'} (places where 2+ analyses overlap), one by one`}>
                  <ClusterInsightsPanel clusters={clusterCards} onFocus={focusFromPanel} recommendations={recommendations}
                    analysis={crashAnalysis} hasCrashData={Boolean(crashes)} crashNoun={crashFilters.excludeAnimal ? 'non-animal crash' : 'crash'}
                    defaultSort="worst" weights={weights} sortTitle={clusterLogic(weights, Boolean(crashes))} />
                </SectionCard>
                {crashes && (
                  <SectionCard track="panel.insights" title="Insights" meta={`${correlatedCount} correlated crash${correlatedCount === 1 ? '' : 'es'} (a deficiency at the crash location), crash by crash`}>
                    <InsightsPanel crashes={crashes} road={shownRoads.length === 1 ? shownRoads[0] : ''} analysis={crashAnalysis}
                      onFocus={focusFromPanel} costTable={costTable} program={program} policyLabel={policyLabel}
                      stats={crashStats} crashNoun={crashFilters.excludeAnimal ? 'non-animal crash' : 'crash'}
                      defaultSort="worst" weights={weights} sortTitle={crashLogic(weights)} />
                  </SectionCard>
                )}
              </div>

              <SectionCard track="panel.strip_chart" title="Milepost strip" meta={`${blocks.length} ${view.unify ? 'road' : 'segment'}${blocks.length === 1 ? '' : 's'} · click a site or an intersection to find it on the map, double-click to open its report`}>
                <OverviewStrips blocks={blocks} sites={sites} clusters={clusters} analyses={analyses}
                  colourBy={view.colourBy} axis={view.axis} onFocus={showOnMap} crashes={crashes} metric={metric} />
              </SectionCard>

              <SectionCard track="panel.clusters" title="Multi-deficiency clusters"
                meta={`${clusters.length} place${clusters.length === 1 ? '' : 's'} where ${view.minAnalyses}+ analyses coincide`}
                tools={(
                  <>
                    <label className="ov-inline" htmlFor="ov-min-an">At least
                      <select id="ov-min-an" className="ov-select" value={view.minAnalyses} onChange={(e) => patchView({ minAnalyses: Number(e.target.value) })}>
                        <option value={2}>2 analyses</option><option value={3}>3 analyses</option><option value={4}>4 analyses</option>
                      </select>
                    </label>
                    <RowsSelect id="ov-rows-clusters" value={view.rowsClusters} onChange={(n) => patchView({ rowsClusters: n })}
                      max={clusters.filter((c) => c.n >= view.minAnalyses).length} />
                  </>
                )}>
                <ClustersTable rows={clusters} position={position} axis={view.axis} unify={view.unify} limit={view.rowsClusters}
                  rank={clusterRank} onMap={onMapClusters} rankTitle={clusterLogic(weights, Boolean(crashes))}
                  sort={sorts.clusters} onSort={(s) => setSorts((x) => ({ ...x, clusters: s }))} onRowClick={showOnMap} {...pinsFor('clusters')} />
              </SectionCard>
              </>
              )}

              {crashes && (
                <SectionCard track="panel.crash_clusters" title="Crash clusters"
                  meta={`${crashClusters.length} place${crashClusters.length === 1 ? '' : 's'} with 2+ crashes within 0.3 mi · "none" = no deficiency site near it`}
                  tools={<RowsSelect id="so-rows-crash-clusters" value={view.rowsCrashClusters} onChange={(n) => patchView({ rowsCrashClusters: n })} max={crashClusters.length} />}>
                  <CrashClustersTable rows={crashClusters} limit={view.rowsCrashClusters}
                    sort={sorts.crashClusters} onSort={(s) => setSorts((x) => ({ ...x, crashClusters: s }))}
                    onRowClick={(cl) => showOnMap(cl)} {...pinsFor('crashClusters')} />
                </SectionCard>
              )}

              {isNewProject && (
                <SectionCard track="panel.hotspots" defaultOpen title={byLength ? `Project prospects · ${hotspotCfg.lengthMi} mi` : 'Hotspots'}
                  meta={byLength
                    ? `${rankedHotspots.length} non-overlapping ${hotspotCfg.lengthMi} mi stretch${rankedHotspots.length === 1 ? '' : 'es'}, best first (a site counts by the part of it inside)${hotspotCfg.byDirection ? ', per direction' : ''}${range ? ` · search area MP ${range[0].toFixed(2)}–${range[1].toFixed(2)}` : ''} · click one to show it and its sites`
                    : `${rankedHotspots.length} stretch${rankedHotspots.length === 1 ? '' : 'es'} with ${hotspotCfg.minSites}+ sites within ${hotspotCfg.gapMi} mi of each other${hotspotCfg.byDirection ? ', per direction' : ''} · click one to show it and its sites`}
                  tools={<RowsSelect id="so-rows-hotspots" value={rowsOf('rowsHotspots')} onChange={(n) => setRows('rowsHotspots', n)} max={rankedHotspots.length} presets={CUSTOMER_PRESETS} />}>
                  <HotspotsTable rows={rankedHotspots} rank={hotspotRank} prefix={hsPrefix} limit={rowsOf('rowsHotspots')} selected={pickedHotspot}
                    sort={sorts.hotspots || { key: 'rank', dir: 'asc' }} onSort={(s) => setSorts((x) => ({ ...x, hotspots: s }))}
                    onScore={setScoreItem}
                    onRowClick={(h) => { setPickedHotspot((cur) => (cur === h.id ? null : h.id)); showOnMap(h); }} />
                </SectionCard>
              )}

              <SectionCard track="panel.summary" title="Deficiency summary"
                meta={hotspotSummary
                  ? `by ${byLength ? 'project prospect' : 'hotspot'} · the ${hotspotSummary.length} shown on the map`
                  : `${summaryRows.length} ${view.sumBy === 'road' ? 'road' : 'segment'}${summaryRows.length === 1 ? '' : 's'}`}
                tools={(
                  <>
                    {!hotspotSummary && <span className="ov-inline">Rows <Seg label="Summary rows" value={view.sumBy} onChange={(v) => patchView({ sumBy: v })} options={[['road', 'Roads'], ['segment', 'Segments']]} /></span>}
                    <span className="ov-inline">Show <Seg label="Summary measure" value={view.measure} onChange={(v) => patchView({ measure: v })} options={[['sites', 'Sites'], ['miles', 'Deficient miles']]} /></span>
                    <RowsSelect id="so-rows-summary" value={rowsOf('rowsSummary')} onChange={(n) => setRows('rowsSummary', n)}
                      max={(hotspotSummary || summaryRows).length} presets={full ? undefined : CUSTOMER_PRESETS} />
                  </>
                )}>
                <SummaryTable rows={hotspotSummary || summaryRows} analyses={analyses} measure={view.measure} by={view.sumBy}
                  rank={hotspotSummary ? hotspotSummaryRank : null} labelHead={hotspotSummary ? (byLength ? 'Prospect' : 'Hotspot') : null}
                  sort={hotspotSummary ? (sorts.hsSummary || { key: 'rank', dir: 'asc' }) : sorts.summary}
                  onSort={(s) => setSorts((x) => (hotspotSummary ? { ...x, hsSummary: s } : { ...x, summary: s }))} limit={rowsOf('rowsSummary')}
                  onRowClick={(r) => showOnMap(r.firstSite)} {...pinsFor('summary')} />
              </SectionCard>

              <SectionCard track="panel.sites" title="Deficiency sites"
                meta={picked ? `${picked.n} sites of ${picked.label}`
                  : isNewProject ? `${hotspotOfSite.size} sites in the ${mapHotspots.length} ${byLength ? 'prospects' : 'hotspots'} on the map, ${byLength ? 'prospect' : 'hotspot'} by ${byLength ? 'prospect' : 'hotspot'}`
                    : `${sites.length} site${sites.length === 1 ? '' : 's'}`}
                tools={(
                  <>
                    {picked && <button type="button" className="safety-btn safety-btn-ghost" onClick={() => setPickedHotspot(null)}>Every site</button>}
                    <RowsSelect id="ov-rows-sites" value={rowsOf('rowsSites')} onChange={(n) => setRows('rowsSites', n)}
                      max={byAnalysisRank ? sitesNav.max : siteRows.length} presets={full ? undefined : CUSTOMER_PRESETS} />
                  </>
                )}>
                <SitesTable rows={picked ? siteRows.filter((r) => picked.sites.some((s) => s.id === r.id))
                  : isNewProject ? siteRows.filter((r) => hotspotOfSite.has(r.id)) : siteRows}
                  group={isNewProject ? { head: byLength ? 'Prospect' : 'Hotspot', of: (r) => (hotspotOfSite.get(r.id) || {}).label || '—',
                    rank: (r) => [(hotspotOfSite.get(r.id) || {}).rank || 1e9, (siteRankOrder || siteRank).get(r.id) ?? 1e9] } : null}
                  position={position} axis={view.axis} limit={rowsOf('rowsSites')} crashMode={Boolean(crashes)}
                  rank={siteRank} rankOrder={siteRankOrder} perAnalysis={byAnalysisRank} onMap={onMapSites} rankTitle={sitesNav.title}
                  notes={siteNotesOf} onNote={setNoteSite} rowFlags={rowFlags} onScore={setScoreItem} scoreOf={(s) => siteScoreParts(s, scoreCrash(s), weights).total}
                  sort={isNewProject ? (sorts.hsSites || { key: 'group', dir: 'asc' }) : sorts.sites}
                  onSort={(s) => setSorts((x) => (isNewProject ? { ...x, hsSites: s } : { ...x, sites: s }))} onRowClick={showOnMap} {...pinsFor('sites')}
                  expand={crashes ? (site) => {
                    const near = crashesNear(site);
                    const card = site.cmf ? <CmfCard rec={site.cmf} exposure={exposure} ratePct={crashFilters.discountRate} /> : null;
                    if (!near.length) return <>{card}<span className="safety-muted">No crash within {NEAR_MI} mi of this site under the current crash filters.</span></>;
                    return (
                      <>
                      {card}
                      <ul className="so-crash-list">
                        {near.map((c) => (
                          <li key={c.id}>
                            <button type="button" className="ov-report" onClick={() => showOnMap(c)}>MP {c.milepost.toFixed(3)}</button>
                            {' '}{c.severity || c.severityClass} · {c.manner || 'crash'} · {c.datetime ? c.datetime.slice(0, 10) : ''}
                            {Object.keys(c.findings || {}).length ? ` · findings: ${Object.keys(c.findings).join(', ')}` : ''}
                          </li>
                        ))}
                      </ul>
                      </>
                    );
                  } : undefined} />
              </SectionCard>

              {cmfActive && (
                <SectionCard track="panel.program" title="Countermeasure program"
                  meta={`ranked · ${costTable?.source || 'crash costs'} · FHWA CMF Clearinghouse`}>
                  <ProgramPanel recommendations={recommendations} exposure={exposure} ratePct={crashFilters.discountRate}
                    costSource={costTable?.source} policyLabel={policyLabel}
                    corridorLabel={overview ? overview.name : (stateSource?.label || 'selection')}
                    onUnitCost={setUnitCost} onFocus={focusFromPanel} {...pinsFor('program')} />
                </SectionCard>
              )}
              {crashes && crashFilters.showCmf && cmfError && (
                <div className="safety-note">Countermeasure library unavailable ({cmfError}); countermeasure columns are hidden.</div>
              )}

              {crashes && (
                <SectionCard track="panel.statistical" title="Statistical support" meta="per road · does the crash pattern follow the deficiencies more than chance would?">
                  <div className="safety-insights-nav safety-verdict-tools">
                    <MultiPicker label="Show verdicts" options={VERDICT_OPTIONS} selected={verdictShow} onChange={setVerdictShow}
                      noun="verdicts" allMeansEmpty title="Show only analyses whose verdict is one of these outcomes" />
                  </div>
                  {crashPayloads.map(([s]) => {
                    const v = verdicts[s.key] || {};
                    const card = (
                      <VerdictCard verdict={v.verdict} loading={Boolean(v.loading)} error={v.error} show={verdictShow}
                        onRecompute={() => recomputeVerdict(s)} recomputing={Boolean(v.recomputing)} />
                    );
                    return crashPayloads.length > 1
                      ? <SubCard key={s.key} defaultOpen={false} title={`${segmentName(s)} · MP ${(s.mpMin || 0).toFixed(1)}–${(s.mpMax || 0).toFixed(1)}`}>{card}</SubCard>
                      : <React.Fragment key={s.key}>{card}</React.Fragment>;
                  })}
                </SectionCard>
              )}

              {crashes && canAllRoads && intersectionState && (
                <SectionCard track="panel.intersections" title="Intersection priorities" meta="district-wide, from the crash snapshot"
                  tools={shownStates.length > 1 ? (
                    <label className="ov-inline" htmlFor="so-ix-state">State
                      <select id="so-ix-state" className="ov-select" value={intersectionState} onChange={(e) => setIxState(e.target.value)}>
                        {shownStates.map((st) => <option key={st} value={st}>{st}</option>)}
                      </select>
                    </label>
                  ) : null}>
                  <IntersectionsPanel stateId={intersectionState} onFocus={focusFromPanel} library={cmfLibrary}
                    policy={crashFilters.cmfPolicy} datasetYears={firstCrashPayload?.datasetYears}
                    showCmf={Boolean(cmfLibrary && crashFilters.showCmf)} fetchIntersections={fetchIntersectionsSO} />
                </SectionCard>
              )}

              {full && hasNetwork && (
                <SectionCard track="panel.network_sources" title="Road network sources"
                  meta={`${networkFeatures.length} road feature${networkFeatures.length === 1 ? '' : 's'} used by the passing-zone rules · what was taken from where`}>
                  <NetworkSources segments={shownSegments} rules={appliedRules} edits={netEdits}
                    canDelete={canDeleteNetwork} onRestored={refreshState} />
                </SectionCard>
              )}
            </>
          )}
        </>
      )}

      {(dialog === 'new' || dialog === 'edit') && (
        <ManagerDialog overview={dialog === 'edit' ? overview : null} onClose={() => setDialog(null)}
          onSaved={(o) => { setDialog(null); loadList(sourceId('ov', o.id)); }}
          onDeleted={() => { setDialog(null); setSourceKey(null); loadList(); }} />
      )}
      <SettingsDialog open={rulesOpen} onClose={() => setRulesOpen(false)} disabledRules={disabledRules} onChange={setCorrelationRules} />
      {scoreItem && (
        <ScoreDialog item={scoreItem} weights={weights} crash={scoreItem.parts ? null : scoreCrash(scoreItem)} onClose={() => setScoreItem(null)}
          rowFlags={rowFlags} logic={siteLogic(weights, Boolean(crashes)).replace(' Set the weights in Settings → Worst offenders.', '')} />
      )}
      {dialog === 'offenders' && (
        <OffendersDialog weights={weights} onChange={(w) => setWeights(saveWeights(w))} onClose={() => setDialog(null)} />
      )}
      {deleting && (
        <RoadElementDeleteDialog feature={deleting} onClose={() => setDeleting(null)}
          onConfirm={async (reason) => {
            await deleteRoadElement(deleting.state, deleting, deleting.segName, reason);
            refreshState(deleting.state);
          }} />
      )}
      {dialog === 'filters' && (
        <FiltersDialog rules={rules} onChange={setRules} onClose={() => setDialog(null)} />
      )}
      {dialog === 'criteria' && (
        <CriteriaDialog settings={severity} onClose={() => setDialog(null)}
          onChange={(next) => { const saved = saveSeveritySettings(next); applySeverityColors(saved); setSeverity(saved); }} />
      )}
      {noteSite && segByKey.get(noteSite.segKey) && (
        <SiteNoteDialog site={noteSite} segment={segByKey.get(noteSite.segKey)} current={siteNotesOf[noteSite.id] || null}
          onSave={(status, text) => saveNote(noteSite, status, text)} onClose={() => setNoteSite(null)} />
      )}
      {dialog === 'pdf' && overview && !full && (
        <SimplePdfDialog overview={overview} onClose={() => setDialog(null)} notes={pdfNotes}
          title={`Safety Dashboard · ${modeOf(mode).label}${mode === 'maintenance' && maintTask ? ` · ${(MAINT_TASKS.find((t) => t.key === maintTask) || {}).label}` : ''}`}
          request={{
            filters: { analyses, segments: segKeys, severities: filters.severities, direction: filters.direction },
            options: {
              axis: 'mp', unify: false, severity, rules: appliedRules, sortSites: { key: 'rank', dir: 'asc' }, rowFlags: pdfRowFlags,
              mpRange: range || undefined, mergeCurves: !curvesByDirection, appendixData: { scoring: pdfScoring() },
              siteOrder: siteOrderIds, siteScores: pdfSiteScores, siteIds: pdfSiteIds, scopeSiteIds: shownMapSites.map((x) => x.id),
              mapSites: shownMapSites.map((x) => x.id), mapClusters: [],
              ...(isNewProject && rankedHotspots.length ? { hotspots: {
                title: byLength ? `Project prospects · ${hotspotCfg.lengthMi} mi` : 'Deficiency hotspots',
                rule: byLength
                  ? `The best non-overlapping ${hotspotCfg.lengthMi} mi stretches; a site counts by the part of it inside the stretch.`
                  : `Stretches with ${hotspotCfg.minSites} or more sites within ${hotspotCfg.gapMi} mi of each other${hotspotCfg.byDirection ? ', per direction' : ', both directions together'}.`,
                total: rankedHotspots.length,
                items: (rowsOf('rowsHotspots') ? rankedHotspots.slice(0, rowsOf('rowsHotspots')) : rankedHotspots).map((h) => ({
                  label: h.label, road: h.segment ? `${h.road} · ${h.segment}` : h.road, directions: h.directions, mpStart: h.mpStart,
                  mpEnd: h.mpEnd, lengthMi: h.lengthMi, n: h.n, analyses: h.analyses, severe: h.severe, perMi: h.perMi, score: h.score,
                  short: h.short, requestedMi: h.requestedMi })),
              } } : {}),
            },
          }} />
      )}
      {dialog === 'pdf' && overview && full && (
        <PdfDialog overview={overview} onClose={() => setDialog(null)} basemap={baseLayer} notes={pdfNotes}
          panels={(() => {
            const verdictRows = crashPayloads.map(([s]) => [`${segmentName(s)} · MP ${(s.mpMin || 0).toFixed(1)}–${(s.mpMax || 0).toFixed(1)}`, verdicts[s.key]]);
            return {
              available: availablePanels({ clusterCards, crashes, program, verdictRows }),
              build: (limit) => buildPdfPanels({
                clusterCards, crashes, crashAnalysis, costTable, recommendations, policyLabel, exposure,
                ratePct: crashFilters.discountRate, weights, verdictRows, limit,
                crashNoun: crashFilters.excludeAnimal ? 'non-animal crash' : 'crash',
              }),
            };
          })()}
          appendixData={{
            scoring: pdfScoring(),
            // what is about crashes goes in the report only when crash data is loaded
            correlation: crashes ? CORRELATION_RULES.map((r) => ({ title: r.title, rule: r.rule, on: !disabledRules.includes(r.key) })) : undefined,
            offenders: { crash: crashes ? crashLogic(weights) : undefined, cluster: clusterLogic(weights, Boolean(crashes)) },
            cmf: !crashes ? undefined : {
              policy: policyLabel, discountRate: crashFilters.discountRate,
              exposureYears: crashOn ? exposure : null,
              datasetYears: firstCrashPayload?.datasetYears ? `${Math.min(...firstCrashPayload.datasetYears)}–${Math.max(...firstCrashPayload.datasetYears)}` : null,
              costs: costTable ? { name: costTable.name, source: costTable.source, values: costTable.values } : null,
            },
          }}
          counts={{ sites: sites.length, clusters: clusters.length, segments: shownSegments.length }}
          request={{
            filters: { analyses, segments: segKeys, severities: filters.severities, direction: filters.direction },
            options: { axis: view.axis, unify: view.unify, rowsSites: view.rowsSites, rowsClusters: view.rowsClusters,
              minAnalyses: view.minAnalyses, sortSites: sorts.sites, sortClusters: sorts.clusters, severity, rules: appliedRules,
              mpRange: range || undefined, mergeCurves: !curvesByDirection,
              // the rank (tables' "rank" sort) and what the map shows (its Top N), so the PDF matches the page
              siteOrder: siteOrderIds, siteScores: pdfSiteScores, clusterOrder: rankedClusters.map((x) => x.id), rowFlags: pdfRowFlags,
              ...(mapTop.sites ? { scopeSiteIds: shownMapSites.map((x) => x.id) } : {}),
              mapSites: mapTop.sites ? mapSites.map((x) => x.id) : null, mapClusters: mapTop.clusters ? mapClusters.map((x) => x.id) : null },
          }} />
      )}
    </div>
  );
}
